import React, { useState, useMemo, useRef, useEffect } from "react";
import { FUTUREDIAL_LOGO } from "./logoBase64";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, Cell, PieChart, Pie
} from "recharts";
import { normalizeRows } from "./utils/normalize";
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";

// Address of the small local backend that talks to the FutureDial/RADI API on our behalf.
// (kept out of the browser: the API username/password never live in this file)
const API_BASE = "";

// ══════════════════════════════════════════════════
// THEME
// ══════════════════════════════════════════════════
const T = {
  bg: "#f1f5f9", card: "#fff", sidebar: "#0f172a", sidebarHov: "#1e293b",
  border: "#e2e8f0", text: "#0f172a", textMid: "#64748b", textLight: "#94a3b8",
  accent: "#2563eb", accentBg: "#eff6ff",
  fail: "#ef4444", failBg: "#fef2f2", pass: "#10b981", passBg: "#f0fdf4",
  warn: "#f59e0b", warnBg: "#fffbeb", purple: "#8b5cf6", purpleBg: "#f5f3ff",
};
const SC = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Preferred display order for well-known tests — used ONLY to keep charts,
// matrices, and pill filters visually stable/familiar across loads. This
// used to be the full, hardcoded list of every test the dashboard would
// ever show (as FT_COLS) — but real data (both CSV and API) regularly
// includes FT_ tests that were never added to that list (GPS Test, Battery
// Health, Sim Card, and others), so those tests' data was silently invisible
// everywhere in the app. See computeFtCols() below, which now derives the
// real list from whatever's actually in the loaded data — this array just
// controls the ORDER known tests appear in, nothing more. Anything not in
// here still shows up, just appended after (alphabetically).
const PREFERRED_FT_ORDER = [
  "FT_AQA_Earpiece", "FT_AQA_Microphone", "FT_AQA_Speaker",
  "FT_Blemish analysis", "FT_Barometer", "FT_Brightness", "FT_BlueTooth", "FT_Burn in",
  "FT_Camera Flash", "FT_Camera Pictures", "FT_Device Vibrate", "FT_Face ID",
  "FT_Front camera", "FT_Hot pixel analysis", "FT_Gyroscope", "FT_Light Sensor",
  "FT_NFC", "FT_Wifi", "FT_Proximity Sensor", "FT_Magnetometer Sensor",
  "FT_Touch Screen", "FT_Uniformity analysis",
  "FT_Volume Up Button", "FT_Volume Down Button",
  "FT_Ring Mute Switch"
];

// Which FT_ columns are actually part of the tested workflow, across
// `rows`? The rule (found by inspecting the data): if a test's SKIP rate is
// 100% — every single transaction that has this field says "SKIP", never a
// real PASS/FAIL/other outcome — that test simply isn't included in this
// client's workflow, so there's no point showing a button for it. A column
// only counts as "in use" if at least one row has something other than
// blank or "SKIP" for it. Also excludes the "_Time" duration companions
// (e.g. FT_GPS Test_Time — not a result column). Known tests come first in
// PREFERRED_FT_ORDER's order purely for display; anything else found in
// the data is appended afterward, alphabetically — nothing is ever
// dropped for not being on that list.
function computeFtCols(rows) {
  const present = new Set();
  (rows || []).forEach(r => {
    Object.keys(r).forEach(k => {
      if (k.startsWith("FT_") && !k.endsWith("_Time") && r[k] !== "" && r[k] != null && r[k] !== "SKIP") present.add(k);
    });
  });
  const known = PREFERRED_FT_ORDER.filter(c => present.has(c));
  const extra = [...present].filter(c => !PREFERRED_FT_ORDER.includes(c)).sort();
  return [...known, ...extra];
}
const TS = { background: "#fff", border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,.08)", fontSize: 12 };

// ══════════════════════════════════════════════════
// FACETED FILTERING
// ══════════════════════════════════════════════════
// Every filter widget (Company, Model, Station…, the date cascade, the test
// result picker) shares one underlying `filters` object. Applying ALL of
// them (including a dimension's own current selection) to decide what THAT
// dimension's dropdown should offer would mean a dropdown could never show
// an alternative to what's already picked. Instead, each widget's option
// list is computed by applying every OTHER active filter and excluding its
// own key(s) — so picking one Company hides Stations/Models that client
// doesn't have everywhere else, while the Company list itself still shows
// every company so you can switch clients freely.
const DATE_FILTER_KEYS = ["_year", "_month", "_dateStr"];
const TEST_FILTER_KEYS = ["_testCols", "_testVal", "_failOnlyTests"];
function applyFiltersExcept(rows, filters, excludeKeys = []) {
  const exclude = new Set(excludeKeys);
  const testKeysExcluded = TEST_FILTER_KEYS.some(k => exclude.has(k));
  return (rows || []).filter(row => {
    const stdOk = Object.entries(filters).every(([key, vals]) => {
      if (exclude.has(key)) return true;
      if (TEST_FILTER_KEYS.includes(key)) return true; // handled as a group below
      return !vals?.length || vals.includes(row[key]);
    });
    if (!stdOk) return false;
    if (testKeysExcluded) return true;
    const testCols = filters._testCols || [];
    const failOnlySelected = filters._failOnlyTests || [];
    const hasTestFilter = testCols.length > 0 || failOnlySelected.length > 0;
    if (!hasTestFilter) return true;
    const testVal = filters._testVal || "FAIL";
    const ftMatch = testCols.length > 0 && testCols.some(col => row[col] === testVal);
    const rowFails = new Set(String(row["result_FAIL"] || "").split(",").map(s => s.trim()));
    const failOnlyMatch = failOnlySelected.length > 0 && failOnlySelected.some(name => rowFails.has(name));
    return ftMatch || failOnlyMatch;
  });
}

// ══════════════════════════════════════════════════
// UTILS
// ══════════════════════════════════════════════════
const pct = (a, b) => b === 0 ? "—" : ((a / b) * 100).toFixed(1) + "%";
const pctN = (a, b) => b === 0 ? 0 : +((a / b) * 100).toFixed(1);
const fmt = v => {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.length > 120 ? s.slice(0, 120) + "…" : s;
};
const avgA = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
const stdA = arr => {
  if (arr.length < 2) return 0;
  const m = avgA(arr);
  return Math.sqrt(arr.reduce((a, b) => a + (b - m) ** 2, 0) / (arr.length - 1));
};
// Shared "how long did this take" formatter, used everywhere the app shows a
// duration — test durations, idle gaps, etc. Renders as "1min30" instead of
// a decimal like "1.5min", and drops the minutes entirely under a minute
// (e.g. "45s") since "0min45" reads worse for something that short.
const fmtDuration = v => {
  if (v == null || isNaN(v)) return "—";
  const totalSec = Math.round(v * 60);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m === 0 ? `${s}s` : `${m}min${String(s).padStart(2, "0")}`;
};
const hBg = r => r === 0 ? "#f0fdf4" : r < 10 ? "#dcfce7" : r < 25 ? "#fefce8" : r < 50 ? "#fff7ed" : "#fef2f2";
const hFg = r => r === 0 ? "#166534" : r < 10 ? "#15803d" : r < 25 ? "#854d0e" : r < 50 ? "#9a3412" : "#991b1b";

function parsePyLog(raw) {
  if (!raw || typeof raw !== "string") return [];
  const s = raw.trim(); if (!s.startsWith("[")) return [];
  try {
    const j = s.replace(/u'([^']*)'/g, (_, v) => JSON.stringify(v))
      .replace(/\bNone\b/g, "null").replace(/\bTrue\b/g, "true").replace(/\bFalse\b/g, "false");
    return JSON.parse(j);
  } catch { return []; }
}

// ══════════════════════════════════════════════════
// BASE COMPONENTS
// ══════════════════════════════════════════════════
function Badge({ v }) {
  const m = { PASS: [T.passBg, T.pass], SUCCESS: [T.passBg, T.pass], FAIL: [T.failBg, T.fail], FAILURE: [T.failBg, T.fail], SKIP: ["#f8fafc", T.textLight] };
  const [bg, fg] = m[v] || ["#f8fafc", T.textMid];
  return <span style={{ background: bg, color: fg, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{v || "—"}</span>;
}

// ══════════════════════════════════════════════════
// ICONS — a small hand-authored, dependency-free line-icon set (Feather/
// Lucide style: 24x24 viewBox, currentColor stroke) that replaces the emoji
// used throughout the sidebar/KPI cards. Keyed by the SAME emoji characters
// already used at call sites across the file, so nothing else has to change
// — only the two or three places that actually render `icon`/`n.icon` swap
// from `<span>{icon}</span>` to `<Icon>{GLYPHS[icon]}</Icon>`. Any emoji not
// in this map just renders as the original emoji (graceful fallback).
// ══════════════════════════════════════════════════
function Icon({ children, size = 16, strokeWidth = 2 }) {
  if (!children) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {children}
    </svg>
  );
}
const GLYPHS = {
  "📊": <><line x1="6" y1="20" x2="6" y2="14" /><line x1="12" y1="20" x2="12" y2="9" /><line x1="18" y1="20" x2="18" y2="4" /></>,
  "🌐": <><circle cx="12" cy="12" r="9" /><line x1="3" y1="12" x2="21" y2="12" /><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" /></>,
  "📋": <><rect x="6" y="4" width="12" height="17" rx="2" /><rect x="9" y="2" width="6" height="4" rx="1" /><line x1="9" y1="11" x2="15" y2="11" /><line x1="9" y1="15" x2="15" y2="15" /></>,
  "📱": <><rect x="7" y="2" width="10" height="20" rx="2" /><line x1="11" y1="18" x2="13" y2="18" /></>,
  "🔬": <><path d="M6 3v6l-4 9a2 2 0 0 0 2 3h16a2 2 0 0 0 2-3l-4-9V3" /><line x1="6" y1="9" x2="14" y2="9" /></>,
  "🔀": <><polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" /></>,
  "🏥": <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></>,
  "🔁": <><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></>,
  "⏰": <><circle cx="12" cy="13" r="8" /><polyline points="12 9 12 13 15 15" /><line x1="9" y1="2" x2="7" y2="4" /><line x1="15" y1="2" x2="17" y2="4" /></>,
  "⏭": <><polygon points="5 4 15 12 5 20 5 4" /><line x1="19" y1="5" x2="19" y2="19" /></>,
  "⏱": <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l3 2" /><line x1="9" y1="2" x2="15" y2="2" /></>,
  "💤": <><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></>,
  "📄": <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></>,
  "❌": <><circle cx="12" cy="12" r="9" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
  "📉": <><polyline points="22 17 13.5 8.5 8.5 13.5 2 7" /><polyline points="16 17 22 17 22 11" /></>,
  "🏭": <><rect x="2" y="3" width="20" height="7" rx="2" /><rect x="2" y="14" width="20" height="7" rx="2" /><line x1="6" y1="6.5" x2="6.01" y2="6.5" /><line x1="6" y1="17.5" x2="6.01" y2="17.5" /></>,
  "⬇": <><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>,
  "⬆": <><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>,
  "±": <><line x1="12" y1="5" x2="12" y2="11" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="9" y1="16" x2="15" y2="16" /></>,
  "🔌": <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></>,
  "📂": <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>,
  "⏳": <><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /><line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" /></>,
  "🔽": <polyline points="6 9 12 15 18 9" />,
  "📅": <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  "⚠": <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  "⚠️": <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  "🗺": <><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></>,
  "📈": <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>,
  "⚖": <><line x1="12" y1="3" x2="12" y2="21" /><path d="M5 7l-3 6a3.5 3.5 0 0 0 7 0z" /><path d="M19 7l-3 6a3.5 3.5 0 0 0 7 0z" /><line x1="5" y1="7" x2="19" y2="7" /></>,
  "👆": <><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /><line x1="13" y1="13" x2="19" y2="19" /></>,
  "🔄": <><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>,
  "🕐": <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l3 2" /><line x1="9" y1="2" x2="15" y2="2" /></>,
  "🖥️": <><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></>,
  "🗂": <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
  "📡": <><path d="M5 13a10 10 0 0 1 14 0" /><path d="M8.5 16.5a5 5 0 0 1 7 0" /><line x1="12" y1="20" x2="12.01" y2="20" /></>,
  "🔍": <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
  "✕": <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  "✓": <polyline points="20 6 9 17 4 12" />,
  "✅": <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
  "↩": <><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>,
};
// Small solid status dot — used for the red/yellow "flagged" indicators in
// Cross-Station, replacing the 🔴/🟡 emoji circles with a flat, theme-
// consistent dot (emoji circles render inconsistently across OS/browsers).
function Dot({ color, size = 8 }) {
  return <span style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", background: color, flexShrink: 0 }} />;
}

// A small "ⓘ" that reveals a plain-language explanation on hover/focus —
// used next to any number, label, or chart that depends on a rule or
// threshold a first-time viewer wouldn't otherwise know about (e.g. "gaps
// over 15 minutes are excluded", "a test only shows a button if it isn't
// 100% skipped"). `width` controls the bubble's width; `align="right"`
// flips the bubble to open leftward for icons near the right edge.
function InfoTip({ children, width = 240, align = "left", size = 13 }) {
  return (
    <span className={`infotip${align === "right" ? " infotip-right" : ""}`} tabIndex={0}>
      <Icon size={size}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="11.5" /><line x1="12" y1="8" x2="12.01" y2="8" /></Icon>
      <span className="infotip-bubble" style={{ width }}>{children}</span>
    </span>
  );
}
// Renders `icon` (an emoji key) as a crisp SVG glyph when we have one mapped,
// falling back to the raw emoji character for anything not yet in GLYPHS —
// so nothing ever silently disappears, it just looks slightly less refined
// until someone adds a matching glyph above.
function AppIcon({ icon, size = 14, spin = false }) {
  if (!icon) return null;
  const g = GLYPHS[icon];
  if (!g) return <span style={{ fontSize: size }}>{icon}</span>;
  return <span className={spin ? "spin" : undefined} style={{ display: "inline-flex" }}><Icon size={size}>{g}</Icon></span>;
}

// Animates a KPI's displayed number counting up/down toward `value` whenever
// it changes, instead of just snapping. Only kicks in for plain numeric-ish
// values (e.g. "1,234", "87.5%", "42"); anything else (station names, "—",
// values with units like "12.3min") is detected and just displayed as-is —
// this never blocks rendering, it only decides whether to animate.
function useCountUp(value, duration = 650) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  useEffect(() => {
    const str = value == null ? "" : String(value);
    const match = str.match(/^(-?[\d,]+(?:\.\d+)?)(%?)$/);
    if (!match) { setDisplay(value); prevRef.current = value; return undefined; }
    const [, numPart, suffix] = match;
    const to = parseFloat(numPart.replace(/,/g, ""));
    const prevStr = prevRef.current == null ? "" : String(prevRef.current);
    const prevMatch = prevStr.match(/^(-?[\d,]+(?:\.\d+)?)(%?)$/);
    const from = prevMatch ? parseFloat(prevMatch[1].replace(/,/g, "")) : 0;
    if (from === to) { setDisplay(value); return undefined; }
    const isInt = Number.isInteger(to) && !numPart.includes(".");
    const hasCommas = numPart.includes(",");
    const start = performance.now();
    let raf;
    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const cur = from + (to - from) * eased;
      const rounded = isInt ? Math.round(cur) : +cur.toFixed(1);
      setDisplay((hasCommas ? rounded.toLocaleString() : String(rounded)) + suffix);
      if (t < 1) raf = requestAnimationFrame(tick);
      else { setDisplay(value); prevRef.current = value; }
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [value, duration]);
  return display;
}

function KpiCard({ label, value, sub, color, icon }) {
  const c = color || T.accent;
  const display = useCountUp(value);
  return (
    <div className="hover-lift" style={{
      background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 20px",
      flex: "1 1 130px", boxShadow: "0 2px 8px rgba(0,0,0,.06)", borderTop: `3px solid ${c}`
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        {icon && (
          <div style={{
            width: 24, height: 24, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${c}, ${c}cc)`, color: "#fff", flexShrink: 0
          }}>
            <AppIcon icon={icon} size={13} />
          </div>
        )}
        <span style={{ color: T.textLight, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>{label}</span>
      </div>
      <div className="tabular-nums" style={{ color: c, fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{display}</div>
      {sub && <div style={{ color: T.textLight, fontSize: 10, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// Splits a "EMOJI rest of text" string into { icon, text } when the leading
// token is one we have a glyph for — used so existing "📊 Some Title"
// strings scattered around the file render as a real icon + text without
// having to touch every call site individually.
function splitLeadingIcon(str) {
  if (typeof str !== "string") return { icon: null, text: str };
  const m = str.match(/^(\S+)\s+(.*)$/s);
  if (m && GLYPHS[m[1]]) return { icon: m[1], text: m[2] };
  return { icon: null, text: str };
}

function Card({ children, style = {}, noPad, title }) {
  const { icon, text } = title ? splitLeadingIcon(title) : { icon: null, text: null };
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
      boxShadow: "0 1px 4px rgba(0,0,0,.05)", padding: noPad ? 0 : 18, overflow: noPad ? "hidden" : undefined, ...style
    }}>
      {title && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, color: T.text, fontWeight: 700, fontSize: 14 }}>
          {icon && <AppIcon icon={icon} size={14} />}
          {text}
        </div>
      )}
      {children}
    </div>
  );
}

function SectionTitle({ children, sub }) {
  const { icon, text } = splitLeadingIcon(children);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 20, background: T.accent, borderRadius: 2 }} />
        <span style={{ color: T.text, fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 7 }}>
          {icon && <AppIcon icon={icon} size={14} />}
          {text}
        </span>
      </div>
      {sub && <div style={{ color: T.textMid, fontSize: 11, marginTop: 4, marginLeft: 14 }}>{sub}</div>}
    </div>
  );
}

function ChartTitle({ children }) {
  return <div style={{ color: T.textMid, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 10 }}>{children}</div>;
}

function SubTab({ tabs, active, onSelect }) {
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
      {tabs.map(([k, l]) => (
        <button key={k} onClick={() => onSelect(k)} style={{
          padding: "7px 16px", borderRadius: 8, fontSize: 12, cursor: "pointer",
          border: `1px solid ${active === k ? T.accent : T.border}`,
          background: active === k ? T.accentBg : T.card,
          color: active === k ? T.accent : T.textMid, fontWeight: active === k ? 700 : 400,
          display: "flex", alignItems: "center", gap: 7
        }}>{l}</button>
      ))}
    </div>
  );
}

function EmptyState({ msg }) {
  return <div style={{ padding: 40, textAlign: "center", color: T.textLight, background: T.card, borderRadius: 12, border: `1px solid ${T.border}`, fontSize: 13 }}>{msg}</div>;
}

// ══════════════════════════════════════════════════
// MULTI-SELECT
// ══════════════════════════════════════════════════
function MultiSelect({ label, options, selected, onChange, formatOption = v => v }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const toggle = v => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const clear = e => { e.stopPropagation(); onChange([]); };
  return (
    <div ref={ref} style={{ position: "relative", minWidth: 120, flex: "1 1 120px" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", background: selected.length ? T.accentBg : T.card,
        border: `1px solid ${selected.length ? T.accent : T.border}`, borderRadius: 8,
        padding: "7px 10px", cursor: "pointer", color: selected.length ? T.accent : T.textMid,
        fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4,
        fontWeight: selected.length ? 600 : 400, boxShadow: "0 1px 2px rgba(0,0,0,.04)"
      }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected.length === 0 ? label : selected.length === 1 ? formatOption(selected[0]) : `${label} (${selected.length})`}
        </span>
        <span style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
          {selected.length > 0 && <span onClick={clear} style={{ color: T.textLight, display: "flex" }}><AppIcon icon="✕" size={10} /></span>}
          <span style={{ fontSize: 8, color: T.textLight }}>{open ? "▲" : "▼"}</span>
        </span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 999,
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 10,
          minWidth: "100%", maxWidth: 260, maxHeight: 240, overflowY: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,.12)"
        }}>
          {options.length === 0 && <div style={{ padding: "12px 14px", color: T.textLight, fontSize: 12 }}>No options</div>}
          {options.map(v => (
            <div key={v} onClick={() => toggle(v)} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer", fontSize: 12,
              background: selected.includes(v) ? T.accentBg : "transparent",
              color: selected.includes(v) ? T.accent : T.text, borderBottom: `1px solid ${T.border}`
            }}>
              <span style={{
                width: 14, height: 14, border: `1.5px solid ${selected.includes(v) ? T.accent : T.border}`,
                borderRadius: 3, background: selected.includes(v) ? T.accent : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fff"
              }}>
                {selected.includes(v) && <AppIcon icon="✓" size={9} strokeWidth={3} />}
              </span>
              {formatOption(v)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// FILTER BAR
// ══════════════════════════════════════════════════
const STD_FILTER_FIELDS = [["Company", "Company"], ["Model", "Model"], ["Manufacturer", "Manufacturer"], ["OS", "OS"], ["Overall status", "Status"],
["Station ID", "Station"], ["Site", "Site"], ["Operator", "Operator"], ["App version", "App ver"]];

// Some client names come through the RADI export with inconsistent casing
// (e.g. "e-Recycle", "rebuy reCommerce GmbH"). This only fixes how they're
// DISPLAYED in the Company filter — matching still uses the raw value, so
// filtering isn't affected.
const COMPANY_LABEL_OVERRIDES = {
  "e-recycle": "E-Recycle",
  "rebuy recommerce gmbh": "Rebuy ReCommerce GmbH",
};
const formatCompanyLabel = v => COMPANY_LABEL_OVERRIDES[String(v).toLowerCase()] || v;

function FilterBar({ data, filters, setFilters, failOnlyTests = [] }) {
  // Faceted per-widget datasets: data narrowed by every filter EXCEPT the
  // one(s) that widget itself controls.
  const dateFacetedData = useMemo(() => applyFiltersExcept(data, filters, DATE_FILTER_KEYS), [data, filters]);
  const testFacetedData = useMemo(() => applyFiltersExcept(data, filters, TEST_FILTER_KEYS), [data, filters]);
  const ftCols = useMemo(() => computeFtCols(testFacetedData), [testFacetedData]);
  const facetedFailOnlyTests = useMemo(() => {
    const present = new Set();
    testFacetedData.forEach(r => {
      String(r["result_FAIL"] || "").split(",").forEach(v => { const name = v.trim(); if (name) present.add(name); });
    });
    return failOnlyTests.filter(name => present.has(name));
  }, [testFacetedData, failOnlyTests]);
  const facetedOptions = useMemo(() => {
    const out = {};
    STD_FILTER_FIELDS.forEach(([key]) => {
      out[key] = [...new Set(applyFiltersExcept(data, filters, [key]).map(r => r[key]).filter(Boolean))].sort();
    });
    return out;
  }, [data, filters]);
  const opts = key => facetedOptions[key] || [];
  const sel = (key, vals) => setFilters(f => ({ ...f, [key]: vals }));
  const activeCount = Object.values(filters).filter(v => v?.length > 0).length;
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: "12px 16px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,.04)"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ color: T.text, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 7 }}>
          <AppIcon icon="🔽" size={12} /> Filters {activeCount > 0 && <span style={{ background: T.accent, color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: 11, marginLeft: 4 }}>{activeCount}</span>}
        </span>
        {activeCount > 0 && <button onClick={() => setFilters({})} style={{
          background: "transparent", border: `1px solid ${T.border}`,
          color: T.textMid, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: 11
        }}>Clear all</button>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {STD_FILTER_FIELDS.map(([key, label]) => (
          <MultiSelect key={key} label={label} options={opts(key)} selected={filters[key] || []} onChange={v => sel(key, v)}
            formatOption={key === "Company" ? formatCompanyLabel : undefined} />
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
        <DateCascadeFilter data={dateFacetedData} filters={filters} setFilters={setFilters} />
        <TestResultFilter filters={filters} setFilters={setFilters} failOnlyTests={facetedFailOnlyTests} ftCols={ftCols} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// DATE CASCADE FILTER
// ══════════════════════════════════════════════════
function DateCascadeFilter({ data, filters, setFilters }) {
  const [open, setOpen] = useState(true);
  const selY = filters._year || [], selM = filters._month || [], selD = filters._dateStr || [];
  const allD = data.filter(r => r._dateStr);
  const years = [...new Set(allD.map(r => r._year).filter(Boolean))].sort();
  const srcM = selY.length ? allD.filter(r => selY.includes(r._year)) : allD;
  const months = [...new Set(srcM.map(r => r._month).filter(Boolean))].sort();
  const srcDay = selM.length ? srcM.filter(r => selM.includes(r._month)) : srcM;
  const days = [...new Set(srcDay.map(r => r._dateStr).filter(Boolean))].sort();
  const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fmtM = m => { const [y, mo] = m.split('-'); return `${MN[+mo - 1]} ${y}`; };
  const hasAny = selY.length > 0 || selM.length > 0 || selD.length > 0;
  const summary = selD.length ? selD.map(d => d.slice(5)).join(', ') : selM.length ? selM.map(fmtM).join(', ') : selY.join(', ');
  const Pill = ({ label, active, count, onClick }) => (
    <button onClick={onClick} title={count ? `${count} records` : undefined} style={{
      padding: '3px 11px', borderRadius: 14, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
      border: `1px solid ${active ? T.accent : T.border}`, background: active ? T.accentBg : '#fff',
      color: active ? T.accent : T.textMid, fontWeight: active ? 700 : 400, transition: 'all .1s'
    }}>
      {label}
      {active && count != null && <span style={{ background: T.accent, color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 9 }}>{count}</span>}
    </button>
  );
  // Count records per pill
  const cntY = y => allD.filter(r => r._year === y).length;
  const cntM = m => srcM.filter(r => r._month === m).length;
  const cntD = d => srcDay.filter(r => r._dateStr === d).length;
  return (
    <div style={{ background: '#f8fafc', border: `1px solid ${hasAny ? T.accent : T.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer',
        background: hasAny ? T.accentBg : '#f8fafc', userSelect: 'none'
      }}>
        <span style={{ display: "flex", color: T.textMid }}><AppIcon icon="📅" size={14} /></span>
        <span style={{ color: hasAny ? T.accent : T.textMid, fontWeight: hasAny ? 700 : 500, fontSize: 12, flex: 1 }}>
          Date {hasAny ? <span style={{ fontSize: 11 }}>— {summary}</span> : ''}
        </span>
        {hasAny && <button onClick={e => { e.stopPropagation(); setFilters(f => ({ ...f, _year: [], _month: [], _dateStr: [] })); }} style={{ background: 'transparent', border: 'none', color: T.textLight, cursor: 'pointer', padding: '0 4px', display: "flex" }}><AppIcon icon="✕" size={11} /></button>}
        <span style={{ color: T.textLight, fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '8px 12px', borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            <span style={{ color: T.textLight, fontSize: 10, minWidth: 38, fontWeight: 700 }}>Year</span>
            {years.map(y => <Pill key={y} label={y} active={selY.includes(y)} count={selY.includes(y) ? cntY(y) : null} onClick={() => {
              const next = selY.includes(y) ? selY.filter(x => x !== y) : [...selY, y];
              setFilters(f => ({ ...f, _year: next, _month: [], _dateStr: [] }));
            }} />)}
          </div>
          {months.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              <span style={{ color: T.textLight, fontSize: 10, minWidth: 38, fontWeight: 700 }}>Month</span>
              {months.map(m => <Pill key={m} label={fmtM(m)} active={selM.includes(m)} count={selM.includes(m) ? cntM(m) : null} onClick={() => {
                const next = selM.includes(m) ? selM.filter(x => x !== m) : [...selM, m];
                setFilters(f => ({ ...f, _month: next, _dateStr: [] }));
              }} />)}
            </div>
          )}
          {selM.length > 0 && days.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              <span style={{ color: T.textLight, fontSize: 10, minWidth: 38, fontWeight: 700 }}>Day</span>
              {days.map(d => <Pill key={d} label={d.slice(8) + ' (' + cntD(d) + ')'} active={selD.includes(d)} onClick={() => {
                const next = selD.includes(d) ? selD.filter(x => x !== d) : [...selD, d];
                setFilters(f => ({ ...f, _dateStr: next }));
              }} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TEST RESULT FILTER
// ══════════════════════════════════════════════════
function TestResultFilter({ filters, setFilters, failOnlyTests = [], ftCols = [] }) {
  const [open, setOpen] = useState(false);
  const selCols = filters._testCols || [];
  const selFailOnly = filters._failOnlyTests || [];
  const selVal = filters._testVal || "FAIL";
  const hasAny = selCols.length > 0 || selFailOnly.length > 0;
  const VALS = ["FAIL", "PASS", "SKIP"];
  const VCOL = { FAIL: T.fail, PASS: T.pass, SKIP: T.textLight };
  const shortName = col => col.replace("FT_", "");
  const toggleCol = col => {
    const next = selCols.includes(col) ? selCols.filter(x => x !== col) : [...selCols, col];
    setFilters(f => ({ ...f, _testCols: next }));
  };
  const toggleFailOnly = name => {
    const next = selFailOnly.includes(name) ? selFailOnly.filter(x => x !== name) : [...selFailOnly, name];
    setFilters(f => ({ ...f, _failOnlyTests: next }));
  };
  const totalSelected = selCols.length + selFailOnly.length;
  const summary = totalSelected ? `${totalSelected} test${totalSelected > 1 ? 's' : ''} selected` : '';
  return (
    <div style={{ background: '#f8fafc', border: `1px solid ${hasAny ? VCOL[selVal] : T.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer',
        background: hasAny ? selVal === "FAIL" ? T.failBg : selVal === "PASS" ? T.passBg : "#f8fafc" : '#f8fafc', userSelect: 'none'
      }}>
        <span style={{ display: "flex", color: T.textMid }}><AppIcon icon="🔬" size={14} /></span>
        <span style={{ color: hasAny ? VCOL[selVal] : T.textMid, fontWeight: hasAny ? 700 : 500, fontSize: 12, flex: 1 }}>
          Test Result {hasAny ? <span style={{ fontSize: 11 }}>— {summary}</span> : ''}
        </span>
        {hasAny && <button onClick={e => { e.stopPropagation(); setFilters(f => ({ ...f, _testCols: [], _failOnlyTests: [] })); }} style={{ background: 'transparent', border: 'none', color: T.textLight, cursor: 'pointer', padding: '0 4px', display: "flex" }}><AppIcon icon="✕" size={11} /></button>}
        <span style={{ color: T.textLight, fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '10px 12px', borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Value picker — applies only to FT_ cols */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: T.textLight, fontSize: 10, fontWeight: 700, minWidth: 38 }}>Value</span>
            {VALS.map(v => (
              <button key={v} onClick={() => setFilters(f => ({ ...f, _testVal: v }))} style={{
                padding: '2px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', fontWeight: selVal === v ? 700 : 400,
                border: `1px solid ${selVal === v ? VCOL[v] : T.border}`, background: selVal === v ? (v === "FAIL" ? T.failBg : v === "PASS" ? T.passBg : "#f8fafc") : 'transparent',
                color: selVal === v ? VCOL[v] : T.textMid
              }}>{v}</button>
            ))}
            <span style={{ color: T.textLight, fontSize: 10, marginLeft: 4 }}>Match: ANY selected</span>
          </div>
          {/* FT_ test pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: -2 }}>
            <span style={{ fontSize: 9, color: T.textLight }}>Only tests that actually ran are listed</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
            {ftCols.map(col => (
              <button key={col} onClick={() => toggleCol(col)} style={{
                padding: '3px 10px', borderRadius: 14, fontSize: 11, cursor: 'pointer',
                border: `1px solid ${selCols.includes(col) ? VCOL[selVal] : T.border}`,
                background: selCols.includes(col) ? (selVal === "FAIL" ? T.failBg : selVal === "PASS" ? T.passBg : "#f5f3ff") : 'transparent',
                color: selCols.includes(col) ? VCOL[selVal] : T.textMid, fontWeight: selCols.includes(col) ? 700 : 400
              }}>{shortName(col)}</button>
            ))}
          </div>
          {/* result_FAIL only tests (no FT_ column) */}
          {failOnlyTests.length > 0 && (
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.textLight, letterSpacing: 0.8 }}>RESULT_FAIL ONLY</span>
                <span style={{ fontSize: 9, color: T.textLight, background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>no FT_ column in the export</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {failOnlyTests.map(name => (
                  <button key={name} onClick={() => toggleFailOnly(name)} style={{
                    padding: '3px 10px', borderRadius: 14, fontSize: 11, cursor: 'pointer',
                    border: `1px solid ${selFailOnly.includes(name) ? T.fail : T.border}`,
                    background: selFailOnly.includes(name) ? T.failBg : 'transparent',
                    color: selFailOnly.includes(name) ? T.fail : T.textMid,
                    fontWeight: selFailOnly.includes(name) ? 700 : 400
                  }}>{name}</button>
                ))}
              </div>
              {selFailOnly.length > 0 && (
                <div style={{ fontSize: 10, color: T.textMid, background: '#f1f5f9', borderRadius: 6, padding: '4px 8px', marginTop: 6 }}>
                  Showing rows where <strong>result_FAIL</strong> contains [{selFailOnly.join(', ')}]
                </div>
              )}
            </div>
          )}
          {totalSelected > 0 && (
            <div style={{ fontSize: 10, color: T.textMid, background: '#f1f5f9', borderRadius: 6, padding: '4px 8px' }}>
              Showing rows where <strong>any</strong> of [{[...selCols.map(shortName), ...selFailOnly].join(', ')}] match
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// DATA TABLE
// ══════════════════════════════════════════════════
function DataTable({ columns, rows, maxRows = 200, search = "", onRowClick }) {
  const [sort, setSort] = useState({ col: null, dir: 1 });
  const lq = search.toLowerCase();
  const safeRows = rows.filter(r => r != null && typeof r === "object");
  const frows = search ? safeRows.filter(r => columns.some(c => String(r[c.key] ?? "").toLowerCase().includes(lq))) : safeRows;
  const sorted = useMemo(() => {
    if (!sort.col) return frows;
    return [...frows].sort((a, b) => {
      const av = a[sort.col], bv = b[sort.col];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
      return String(av ?? "").localeCompare(String(bv ?? "")) * sort.dir;
    });
  }, [frows, sort]);
  return (
    <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${T.border}` }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#f8fafc" }}>
            {columns.map(c => (
              <th key={c.key} onClick={() => setSort(s => ({ col: c.key, dir: s.col === c.key ? -s.dir : 1 }))}
                style={{
                  padding: "10px 14px", color: T.textMid, fontWeight: 700, textAlign: c.right ? "right" : "left",
                  cursor: "pointer", whiteSpace: "nowrap", borderBottom: `1px solid ${T.border}`, fontSize: 11, userSelect: "none"
                }}>
                {c.label}{sort.col === c.key ? (sort.dir === 1 ? " ↑" : " ↓") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, maxRows).map((row, i) => (
            <tr key={i} onClick={() => onRowClick?.(row)}
              style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc", cursor: onRowClick ? "pointer" : "default" }}
              onMouseEnter={e => e.currentTarget.style.background = T.accentBg}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafbfc"}>
              {columns.map(c => {
                const cellColor = c.highlight && row[c.key] ? T.fail : T.text;
                return (
                  <td key={c.key} style={{
                    padding: "8px 14px", color: cellColor, textAlign: c.right ? "right" : "left",
                    borderBottom: `1px solid ${T.border}`, fontFamily: c.mono ? "monospace" : undefined,
                  }}>
                    {c.badge ? <Badge v={row[c.key]} /> : fmt(row[c.key])}
                  </td>
                );
              })}
            </tr>
          ))}
          {sorted.length > maxRows && <tr><td colSpan={columns.length} style={{ padding: 10, color: T.textLight, textAlign: "center", fontSize: 11 }}>... {sorted.length - maxRows} more rows</td></tr>}
          {sorted.length === 0 && <tr><td colSpan={columns.length} style={{ padding: 24, color: T.textLight, textAlign: "center" }}>No results</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════
// TEST PICKER
// ══════════════════════════════════════════════════
function TestPicker({ selected, onSelect, ftCols = [] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 14, alignItems: "center" }}>
      {ftCols.map(col => {
        const label = col.replace("FT_", ""); const active = selected === col;
        return (
          <button key={col} onClick={() => onSelect(col)} style={{
            padding: "4px 11px", borderRadius: 20, fontSize: 11, cursor: "pointer",
            border: `1px solid ${active ? T.accent : T.border}`,
            background: active ? T.accentBg : T.card, color: active ? T.accent : T.textMid, fontWeight: active ? 700 : 400
          }}>{label}</button>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: TEST ANALYSIS
// ══════════════════════════════════════════════════
function TestAnalysisTab({ data }) {
  const ftCols = useMemo(() => computeFtCols(data), [data]);
  const [sel, setSel] = useState(() => ftCols[0] || "");
  // Keep `sel` valid as the underlying data/filters change — ftCols is no
  // longer a fixed list, so the previously-selected test might not exist in
  // a newly loaded/filtered dataset.
  useEffect(() => { if (ftCols.length && !ftCols.includes(sel)) setSel(ftCols[0]); }, [ftCols]); // eslint-disable-line react-hooks/exhaustive-deps
  const analysis = useMemo(() => {
    const failRows = data.filter(r => r[sel] === "FAIL");
    const coFails = {};
    failRows.forEach(row => ftCols.forEach(col => { if (col !== sel && row[col] === "FAIL") coFails[col] = (coFails[col] || 0) + 1; }));
    const byStation = {};
    failRows.forEach(row => {
      const s = row["Station ID"] || "Unknown", m = row["Model"] || "Unknown";
      if (!byStation[s]) byStation[s] = { total: 0, models: {} };
      byStation[s].total++; byStation[s].models[m] = (byStation[s].models[m] || 0) + 1;
    });
    return {
      failRows, byStation, chartData: Object.entries(byStation).map(([s, v]) => ({ name: s, FAIL: v.total })),
      topCoFails: Object.entries(coFails).sort((a, b) => b[1] - a[1]).slice(0, 6)
    };
  }, [data, sel]);
  return (
    <div>
      <TestPicker selected={sel} onSelect={setSel} ftCols={ftCols} />
      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <Card style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 20px" }}>
          <span style={{ color: T.textMid, fontSize: 12 }}>Total FAIL — <strong style={{ color: T.accent }}>{sel.replace("FT_", "")}</strong></span>
          <span style={{ color: T.fail, fontWeight: 800, fontSize: 24 }}>{analysis.failRows.length}</span>
        </Card>
        {analysis.topCoFails.length > 0 && (
          <Card style={{ flex: 1, padding: "12px 16px" }}>
            <div style={{ color: T.warn, fontSize: 11, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><AppIcon icon="⚠" size={12} /> Some failing devices also failed:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {analysis.topCoFails.map(([col, cnt]) => (
                <span key={col} style={{ background: T.warnBg, color: T.warn, borderRadius: 12, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                  {col.replace("FT_", "")} ×{cnt}
                </span>
              ))}
            </div>
          </Card>
        )}
      </div>
      {analysis.chartData.length > 0 && (
        <Card style={{ marginBottom: 14, padding: "14px 8px" }}>
          <ChartTitle>Fails by Station</ChartTitle>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={analysis.chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="name" tick={{ fill: T.textMid, fontSize: 11 }} /><YAxis tick={{ fill: T.textMid, fontSize: 11 }} />
              <Tooltip contentStyle={TS} /><Bar dataKey="FAIL" fill={T.fail} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
      <Card noPad>
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: T.text, fontWeight: 700 }}>{sel.replace("FT_", "")}</span>
          <span style={{ color: T.fail, fontWeight: 800 }}>{analysis.failRows.length}</span>
        </div>
        {Object.entries(analysis.byStation).map(([station, sv]) => (
          <div key={station}>
            <div style={{ padding: "8px 16px", background: "#f8fafc", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: T.accent, fontWeight: 700, fontSize: 12 }}>{station}</span>
              <span style={{ color: T.accent, fontWeight: 700 }}>{sv.total}</span>
            </div>
            {Object.entries(sv.models).sort((a, b) => b[1] - a[1]).map(([model, cnt]) => (
              <div key={model} style={{ padding: "6px 32px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: T.text, fontSize: 12 }}>{model}</span>
                <span style={{ color: T.textMid, fontWeight: 600 }}>{cnt}</span>
              </div>
            ))}
          </div>
        ))}
        {Object.keys(analysis.byStation).length === 0 && <div style={{ padding: 24, color: T.textLight, textAlign: "center" }}>No FAIL results for this test</div>}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: CROSS-STATION
// ══════════════════════════════════════════════════
function ThresholdRow({ label, color, value, onChange }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flex: "1 1 240px",
      background: T.card, border: `1px solid ${color}44`, borderRadius: 10, padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,.04)"
    }}>
      <span style={{ color, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{label}</span>
      <input type="range" min={1} max={100} value={value} onChange={e => onChange(+e.target.value)} style={{ accentColor: color, flex: 1, minWidth: 60 }} />
      <input type="number" min={1} max={500} value={value} onChange={e => onChange(Math.max(1, +e.target.value))}
        style={{ background: "#f8fafc", border: `1px solid ${color}55`, borderRadius: 6, color, width: 50, padding: "4px 8px", fontSize: 13, fontWeight: 800, textAlign: "center" }} />
    </div>
  );
}

function CrossStationTab({ data }) {
  const ftCols = useMemo(() => computeFtCols(data), [data]);
  const [sel, setSel] = useState(() => ftCols[1] || ftCols[0] || "");
  useEffect(() => { if (ftCols.length && !ftCols.includes(sel)) setSel(ftCols[0]); }, [ftCols]); // eslint-disable-line react-hooks/exhaustive-deps
  const [multiThr, setMultiThr] = useState(3);
  const [singleThr, setSingleThr] = useState(5);
  const [showRed, setShowRed] = useState(true);
  const [showYellow, setShowYellow] = useState(true);
  const [notes, setNotes] = useState({});

  const analysis = useMemo(() => {
    const failRows = data.filter(r => r[sel] === "FAIL");
    const stList = [...new Set(failRows.map(r => r["Station ID"] || "Unknown"))].sort();
    const smMap = {};
    stList.forEach(s => { smMap[s] = {}; });
    failRows.forEach(row => { const s = row["Station ID"] || "Unknown", m = row["Model"] || "Unknown"; smMap[s][m] = (smMap[s][m] || 0) + 1; });
    const mStats = {};
    stList.forEach(s => Object.entries(smMap[s]).forEach(([m, cnt]) => {
      if (!mStats[m]) mStats[m] = { ss: new Set(), total: 0 };
      mStats[m].ss.add(s); mStats[m].total += cnt;
    }));
    const isRed = m => mStats[m]?.ss.size >= 2 && mStats[m]?.total >= multiThr;
    const isYellow = (s, m, cnt) => cnt >= singleThr;
    return { stList, smMap, mStats, isRed, isYellow, total: failRows.length };
  }, [data, sel, multiThr, singleThr]);

  const redCount = useMemo(() => {
    const seen = new Set();
    analysis.stList.forEach(s => Object.keys(analysis.smMap[s]).forEach(m => { if (analysis.isRed(m)) seen.add(m); }));
    return seen.size;
  }, [analysis]);

  return (
    <div>
      <TestPicker selected={sel} onSelect={setSel} ftCols={ftCols} />
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ color: T.textMid, fontSize: 11 }}>A model is flagged when its failures for the selected test cross one of these thresholds:</span>
        <InfoTip width={290}>
          <strong style={{ color: "#fca5a5" }}>Red (multiple stations)</strong>: this model failed on <strong>2 or more different stations</strong>, with total fails across all of them at or above the red threshold — suggests a real device/model issue, not one flaky station.
          <br /><br />
          <strong style={{ color: "#fcd34d" }}>Yellow (single station)</strong>: this model failed a lot on <strong>one specific station</strong> — suggests that station, not the model, may be the problem.
        </InfoTip>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <ThresholdRow label={<><Dot color={T.fail} /> Multiple stations — min total fails</>} color={T.fail} value={multiThr} onChange={setMultiThr} />
        <ThresholdRow label={<><Dot color={T.warn} /> Single station — min fails</>} color={T.warn} value={singleThr} onChange={setSingleThr} />
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ color: T.textMid, fontSize: 11, fontWeight: 600 }}>Show:</span>
        {[{ k: "red", l: <>Multiple stations ({redCount} models)</>, c: T.fail, v: showRed, s: setShowRed },
        { k: "yel", l: <>High single-station</>, c: T.warn, v: showYellow, s: setShowYellow }
        ].map(({ k, l, c, v, s }) => (
          <button key={k} onClick={() => s(x => !x)} style={{
            padding: "5px 14px", borderRadius: 20, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            border: `1px solid ${v ? c + "88" : T.border}`, background: v ? c + "15" : T.card, color: v ? c : T.textMid, fontWeight: v ? 700 : 400
          }}>
            <Dot color={c} size={7} />
            {v && <AppIcon icon="✓" size={10} strokeWidth={3} />}
            {l}
          </button>
        ))}
        <div style={{ marginLeft: "auto", background: T.failBg, borderRadius: 8, padding: "5px 14px" }}>
          <span style={{ color: T.textMid, fontSize: 11 }}>Total FAIL </span>
          <span style={{ color: T.fail, fontWeight: 800 }}>{analysis.total}</span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: analysis.stList.length > 1 ? "repeat(auto-fit,minmax(290px,1fr))" : "1fr", gap: 14 }}>
        {analysis.stList.map(station => {
          const stTotal = Object.values(analysis.smMap[station]).reduce((a, b) => a + b, 0);
          return (
            <Card key={station} noPad>
              <div style={{ padding: "10px 16px", background: "#f8fafc", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: T.accent, fontWeight: 800, fontSize: 13 }}>{station}</span>
                <span style={{ color: T.accent, fontWeight: 800 }}>{stTotal}</span>
              </div>
              {Object.entries(analysis.smMap[station]).sort((a, b) => b[1] - a[1]).map(([model, cnt]) => {
                const red = analysis.isRed(model);
                const yellow = !red && analysis.isYellow(station, model, cnt);
                const both = red && analysis.isYellow(station, model, cnt);
                const hRed = (red || both) && showRed, hYellow = yellow && showYellow;
                const highlighted = hRed || hYellow;
                const rc = hRed ? T.fail : hYellow ? T.warn : null;
                const noteKey = `${sel}::${station}::${model}`;
                return (
                  <div key={model} style={{ borderBottom: `1px solid ${T.border}`, background: hRed ? T.failBg : hYellow ? T.warnBg : "transparent" }}>
                    <div style={{ padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                        {hRed && <Dot color={T.fail} />}
                        {both && showYellow && <Dot color={T.warn} />}
                        {hYellow && !both && <Dot color={T.warn} />}
                        <span style={{ color: rc || T.text, fontSize: 12, fontWeight: highlighted ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{model}</span>
                        {hRed && <span style={{ color: T.textLight, fontSize: 10, flexShrink: 0 }}>(Σ{analysis.mStats[model]?.total})</span>}
                      </div>
                      <span style={{ color: rc || T.textMid, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{cnt}</span>
                    </div>
                    {highlighted && (
                      <div style={{ padding: "0 16px 8px" }}>
                        <input value={notes[noteKey] || ""} onChange={e => setNotes(n => ({ ...n, [noteKey]: e.target.value }))}
                          placeholder="Add note..."
                          style={{
                            width: "100%", background: "#fff", border: `1px solid ${rc}44`, borderRadius: 6,
                            color: T.text, fontSize: 11, padding: "5px 10px", outline: "none", boxSizing: "border-box"
                          }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          );
        })}
      </div>
      {analysis.total === 0 && <EmptyState msg={`No FAIL results for ${sel.replace("FT_", "")}`} />}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: STATION HEALTH
// ══════════════════════════════════════════════════
function StationHealthTab({ data }) {
  const [view, setView] = useState("heatmap");
  const ftCols = useMemo(() => computeFtCols(data), [data]);

  const stations = useMemo(() => [...new Set(data.map(r => r["Station ID"] || "Unknown").filter(Boolean))].sort(), [data]);

  const heatmap = useMemo(() => {
    const cells = {};
    stations.forEach(s => { cells[s] = {}; ftCols.forEach(col => { cells[s][col] = { f: 0, p: 0, sk: 0, t: 0 }; }); });
    data.forEach(row => {
      const s = row["Station ID"] || "Unknown";
      ftCols.forEach(col => {
        const v = row[col]; if (!v || v === "") return;
        const c = cells[s]?.[col]; if (!c) return;
        c.t++;
        if (v === "FAIL") c.f++; else if (v === "PASS") c.p++; else if (v === "SKIP") c.sk++;
      });
    });
    return cells;
  }, [data, stations, ftCols]);

  const driftData = useMemo(() => {
    const bds = {};
    data.forEach(row => {
      const d = row._dateStr; if (!d) return;
      const s = row["Station ID"] || "Unknown";
      const key = d + "::" + s;
      if (!bds[key]) bds[key] = { date: d, station: s, total: 0, fail: 0 };
      bds[key].total++;
      if (row["Overall status"] === "FAILURE") bds[key].fail++;
    });
    const dates = [...new Set(Object.values(bds).map(v => v.date))].sort();
    const series = stations.map(s => ({ station: s, data: dates.map(d => { const v = bds[d + "::" + s]; return { date: d, failPct: v ? pctN(v.fail, v.total) : null }; }) }));
    return { dates, series };
  }, [data, stations]);

  const consistencyData = useMemo(() => {
    const models = [...new Set(data.map(r => r["Model"]).filter(Boolean))].sort();
    const matrix = {};
    models.forEach(m => {
      matrix[m] = {}; stations.forEach(s => {
        const rows = data.filter(r => r["Model"] === m && (r["Station ID"] || "Unknown") === s);
        matrix[m][s] = rows.length ? pctN(rows.filter(r => r["Overall status"] === "SUCCESS").length, rows.length) : null;
      });
    });
    const multiModels = models.filter(m => stations.filter(s => matrix[m][s] !== null).length >= 2);
    const withVariance = multiModels.map(m => {
      const vals = stations.map(s => matrix[m][s]).filter(v => v !== null);
      return { model: m, spread: +(Math.max(...vals) - Math.min(...vals)).toFixed(1), vals: Object.fromEntries(stations.map(s => [s, matrix[m][s]])) };
    }).sort((a, b) => b.spread - a.spread);
    return { withVariance };
  }, [data, stations]);

  const durationData = useMemo(() => {
    if (!data.length) return { tests: [], stats: {} };
    const keys = Object.keys(data[0]);
    const tCols = keys.filter(k => k.endsWith("_Time") && !k.includes("Server") && data.some(r => +r[k] > 0));
    const stats = {};
    tCols.forEach(tc => {
      stats[tc] = {}; stations.forEach(s => {
        const vals = data.filter(r => (r["Station ID"] || "Unknown") === s && +r[tc] > 0).map(r => +r[tc]);
        if (vals.length) stats[tc][s] = { mean: avgA(vals), std: stdA(vals), n: vals.length };
      });
    });
    return { tests: tCols, stats };
  }, [data, stations]);

  return (
    <div>
      <SubTab tabs={[
        ["heatmap", <><AppIcon icon="🗺" size={12} /> Test Heatmap</>],
        ["drift", <><AppIcon icon="📈" size={12} /> Drift Over Time</>],
        ["consistency", <><AppIcon icon="⚖" size={12} /> Consistency</>],
        ["duration", <><AppIcon icon="⏱" size={12} /> Duration Anomaly</>],
      ]} active={view} onSelect={setView} />

      {view === "heatmap" && (
        <Card noPad>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: T.text, fontWeight: 700 }}>Fail Rate — Station × Test Component</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11 }}>
              {[[0, "0%", "#f0fdf4", "#166534"], [15, "low", "#fefce8", "#854d0e"], [40, "med", "#fff7ed", "#9a3412"], [80, "high", "#fef2f2", "#991b1b"]].map(([, l, bg, fg]) => (
                <span key={l} style={{ background: bg, color: fg, borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>{l}</span>
              ))}
            </div>
          </div>
          <div style={{ overflowX: "auto", padding: 12 }}>
            <table style={{ borderCollapse: "separate", borderSpacing: 3, fontSize: 10 }}>
              <thead>
                <tr>
                  <th style={{ padding: "4px 8px", color: T.textLight, textAlign: "left", fontWeight: 600, minWidth: 80 }}>Station</th>
                  {ftCols.map(col => (
                    <th key={col} style={{
                      padding: "2px 4px", color: T.textMid, fontWeight: 600, textAlign: "center",
                      writingMode: "vertical-rl", transform: "rotate(180deg)", height: 90, whiteSpace: "nowrap", verticalAlign: "bottom"
                    }}>
                      {col.replace("FT_", "")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stations.map(s => (
                  <tr key={s}>
                    <td style={{ padding: "4px 8px", color: T.accent, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>{s}</td>
                    {ftCols.map(col => {
                      const c = heatmap[s]?.[col];
                      if (!c || c.t === 0) return <td key={col} style={{ background: "#f1f5f9", borderRadius: 4, width: 26, height: 22, textAlign: "center" }} />;
                      const rate = pctN(c.f, c.t);
                      return (
                        <td key={col} title={`${s} / ${col.replace("FT_", "")}: ${rate}% fail (${c.f}/${c.t})`}
                          style={{ background: hBg(rate), borderRadius: 4, width: 26, height: 22, textAlign: "center", cursor: "default" }}>
                          {rate > 0 && <span style={{ color: hFg(rate), fontSize: 9, fontWeight: 700 }}>{Math.round(rate)}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {view === "drift" && (
        <Card>
          <ChartTitle>Fail Rate Over Time — Per Station (%)</ChartTitle>
          {driftData.dates.length < 2 ? <EmptyState msg="Need 2+ date points to show drift" /> : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis dataKey="date" type="category" allowDuplicatedCategory={false} tick={{ fill: T.textMid, fontSize: 10 }} />
                <YAxis tick={{ fill: T.textMid, fontSize: 10 }} unit="%" domain={[0, 100]} />
                <Tooltip contentStyle={TS} formatter={v => [v + "%", "Fail rate"]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {driftData.series.map((s, i) => (
                  <Line key={s.station} data={s.data} type="monotone" dataKey="failPct" name={s.station}
                    stroke={SC[i % SC.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {view === "consistency" && (
        <div>
          <SectionTitle sub="Models on multiple stations — high spread = station bias">Model Pass Rate by Station</SectionTitle>
          {consistencyData.withVariance.length === 0 ? <EmptyState msg="No models found on multiple stations" /> : (
            <DataTable columns={[
              { key: "model", label: "Model" },
              ...stations.map(s => ({ key: `v_${s}`, label: s, right: true })),
              { key: "spread", label: "Spread (pp)", right: true },
            ]} rows={consistencyData.withVariance.slice(0, 60).map(r => ({
              model: r.model, spread: r.spread + "%",
              ...Object.fromEntries(stations.map(s => [`v_${s}`, r.vals[s] != null ? r.vals[s].toFixed(1) + "%" : "—"]))
            }))} />
          )}
        </div>
      )}

      {view === "duration" && (
        <div>
          <SectionTitle sub="Avg test duration per station (ms) — ⚠ marks stations 30%+ above average">Test Duration Anomaly</SectionTitle>
          {durationData.tests.length === 0 ? <EmptyState msg="No *_Time columns found" /> : (
            <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${T.border}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ background: "#f8fafc" }}>
                  <th style={{ padding: "10px 14px", color: T.textMid, fontWeight: 700, textAlign: "left", borderBottom: `1px solid ${T.border}`, fontSize: 11 }}>Test</th>
                  {stations.map(s => <th key={s} style={{ padding: "10px 14px", color: T.accent, fontWeight: 700, textAlign: "right", borderBottom: `1px solid ${T.border}`, fontSize: 11 }}>{s}</th>)}
                </tr></thead>
                <tbody>
                  {durationData.tests.slice(0, 30).map((tc, i) => {
                    const stVals = stations.map(s => durationData.stats[tc]?.[s]?.mean || 0);
                    const maxVal = Math.max(...stVals.filter(v => v > 0), 1);
                    return (
                      <tr key={tc} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                        <td style={{ padding: "8px 14px", color: T.text, borderBottom: `1px solid ${T.border}`, fontSize: 11 }}>{tc.replace("FT_", "").replace("_Time", "")}</td>
                        {stations.map(s => {
                          const stat = durationData.stats[tc]?.[s];
                          if (!stat) return <td key={s} style={{ padding: "8px 14px", textAlign: "right", color: T.textLight, borderBottom: `1px solid ${T.border}` }}>—</td>;
                          const isHigh = stat.mean > maxVal * 1.3 && stVals.filter(v => v > 0).length > 1;
                          return (
                            <td key={s} style={{
                              padding: "8px 14px", textAlign: "right", color: isHigh ? T.warn : T.text,
                              fontWeight: isHigh ? 700 : 400, borderBottom: `1px solid ${T.border}`, background: isHigh ? T.warnBg : "transparent"
                            }}>
                              {(stat.mean / 1000).toFixed(2)}s{isHigh && <AppIcon icon="⚠" size={10} />}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: GLOBAL ANALYSIS
// ══════════════════════════════════════════════════
function GlobalAnalysisTab({ data }) {
  const [view, setView] = useState("station");
  const ftCols = useMemo(() => computeFtCols(data), [data]);

  // ── Station data ──
  const stationData = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const s = r["Station ID"] || "Unknown";
      if (!map[s]) map[s] = { name: s, SUCCESS: 0, FAILURE: 0 };
      map[s][r["Overall status"] === "SUCCESS" ? "SUCCESS" : "FAILURE"]++;
    });
    return Object.values(map).map(r => ({ ...r, total: r.SUCCESS + r.FAILURE, failPct: pctN(r.FAILURE, r.SUCCESS + r.FAILURE) }))
      .sort((a, b) => b.total - a.total);
  }, [data]);

  // ── Model data ──
  const modelData = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const m = r["Model"] || "Unknown";
      if (!map[m]) map[m] = { name: m, SUCCESS: 0, FAILURE: 0 };
      map[m][r["Overall status"] === "SUCCESS" ? "SUCCESS" : "FAILURE"]++;
    });
    return Object.values(map).map(r => ({ ...r, total: r.SUCCESS + r.FAILURE, failPct: pctN(r.FAILURE, r.SUCCESS + r.FAILURE) }))
      .sort((a, b) => b.total - a.total).slice(0, 20);
  }, [data]);

  // ── Test component data ──
  const testData = useMemo(() => {
    return ftCols.map(col => {
      let pass = 0, fail = 0, skip = 0;
      data.forEach(r => {
        const v = r[col];
        if (v === "PASS") pass++;
        else if (v === "FAIL") fail++;
        else if (v === "SKIP") skip++;
      });
      const total = pass + fail + skip;
      return { name: col.replace("FT_", ""), pass, fail, skip, total, failPct: pctN(fail, total) };
    }).filter(r => r.total > 0).sort((a, b) => b.fail - a.fail);
  }, [data, ftCols]);

  const VIEWS = [
    ["station", <><AppIcon icon="🏭" size={12} /> By Station</>],
    ["model", <><AppIcon icon="📱" size={12} /> By Model</>],
    ["tests", <><AppIcon icon="🔬" size={12} /> By Test</>],
  ];
  const curData = view === "station" ? stationData : view === "model" ? modelData : testData;
  const maxHeight = Math.max(curData.length * 38, 220);

  return (
    <div>
      <SubTab tabs={VIEWS} active={view} onSelect={setView} />

      {/* Stacked bar chart */}
      <Card style={{ marginBottom: 18, padding: "16px 8px" }}>
        <ChartTitle>
          {view === "station" ? "Fail vs Success — by Station (sorted by volume)" :
            view === "model" ? "Fail vs Success — by Model (top 20 by volume)" :
              "Pass / Fail / Skip — by Test Component"}
        </ChartTitle>
        <ResponsiveContainer width="100%" height={maxHeight}>
          <BarChart data={curData} layout="vertical" margin={{ left: 130, right: 60, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
            <XAxis type="number" tick={{ fill: T.textMid, fontSize: 10 }} />
            <YAxis type="category" dataKey="name" tick={{ fill: T.text, fontSize: 11 }} width={128} />
            <Tooltip contentStyle={TS}
              formatter={(val, name) => [val.toLocaleString(), name]} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            {view === "tests" ? (
              <>
                <Bar dataKey="pass" name="PASS" fill={T.pass} stackId="s" radius={[0, 0, 0, 0]} />
                <Bar dataKey="fail" name="FAIL" fill={T.fail} stackId="s" radius={[0, 0, 0, 0]} />
                <Bar dataKey="skip" name="SKIP" fill={T.textLight} stackId="s" radius={[0, 4, 4, 0]} />
              </>
            ) : (
              <>
                <Bar dataKey="SUCCESS" name="SUCCESS" fill={T.pass} stackId="s" radius={[0, 0, 0, 0]} />
                <Bar dataKey="FAILURE" name="FAILURE" fill={T.fail} stackId="s" radius={[0, 4, 4, 0]} />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Fail rate bar */}
      <Card style={{ marginBottom: 18, padding: "16px 8px" }}>
        <ChartTitle>Fail Rate % — {view === "station" ? "by Station" : view === "model" ? "by Model" : "by Test"}</ChartTitle>
        <ResponsiveContainer width="100%" height={maxHeight}>
          <BarChart data={curData} layout="vertical" margin={{ left: 130, right: 60, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
            <XAxis type="number" tick={{ fill: T.textMid, fontSize: 10 }} unit="%" domain={[0, 100]} />
            <YAxis type="category" dataKey="name" tick={{ fill: T.text, fontSize: 11 }} width={128} />
            <Tooltip contentStyle={TS} formatter={v => [v + "%", "Fail rate"]} />
            <Bar dataKey="failPct" name="Fail %" radius={[0, 4, 4, 0]}>
              {curData.map((e, i) => (
                <Cell key={i} fill={e.failPct >= 50 ? T.fail : e.failPct >= 20 ? T.warn : T.pass} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Summary table */}
      <DataTable
        columns={view === "tests" ? [
          { key: "name", label: "Test Component" },
          { key: "pass", label: "PASS", right: true },
          { key: "fail", label: "FAIL", right: true },
          { key: "skip", label: "SKIP", right: true },
          { key: "total", label: "Total", right: true },
          { key: "failPct", label: "Fail %", right: true },
        ] : [
          { key: "name", label: view === "station" ? "Station" : "Model" },
          { key: "SUCCESS", label: "SUCCESS", right: true },
          { key: "FAILURE", label: "FAILURE", right: true },
          { key: "total", label: "Total", right: true },
          { key: "failPct", label: "Fail %", right: true },
        ]}
        rows={curData.map(r => ({ ...r, failPct: r.failPct.toFixed(1) + "%" }))}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: SIGNAL QUALITY (removed — kept as stub)
// ══════════════════════════════════════════════════
const AQA_METRICS = [
  { key: "earpiece_snr", label: "Earpiece SNR", unit: "dB", threshold: -6 },
  { key: "speaker_snr", label: "Speaker SNR", unit: "dB", threshold: -6 },
  { key: "mic0_snr", label: "Mic 0 SNR", unit: "dB", threshold: 4 },
  { key: "mic1_snr", label: "Mic 1 SNR", unit: "dB", threshold: 4 },
  { key: "mic2_snr", label: "Mic 2 SNR", unit: "dB", threshold: 4 },
  { key: "mic0_thd", label: "Mic THD", unit: "", threshold: 0.1 },
];

function SignalQualityTab({ data }) {
  const [metric, setMetric] = useState("earpiece_snr");
  const cur = AQA_METRICS.find(m => m.key === metric) || AQA_METRICS[0];

  const parsed = useMemo(() => {
    const rows = [];
    data.forEach(row => {
      const log = parsePyLog(row["logFile"] || row["result"] || "");
      log.forEach(entry => {
        const tn = (entry.tn || "").trim();
        const s = row["Station ID"] || "Unknown", m = row["Model"] || "Unknown", d = row._dateStr || "";
        const td = entry.tData || {};
        if (tn === "AQA_Earpiece" && td.snr != null) rows.push({ type: "earpiece_snr", station: s, model: m, date: d, value: parseFloat(td.snr), status: entry.s });
        if (tn === "AQA_Speaker" && td.snr != null) rows.push({ type: "speaker_snr", station: s, model: m, date: d, value: parseFloat(td.snr), status: entry.s });
        if (tn === "AQA_Microphone") {
          if (td.mic0ch0snr != null) rows.push({ type: "mic0_snr", station: s, model: m, date: d, value: parseFloat(td.mic0ch0snr), status: entry.s });
          if (td.mic1ch0snr != null) rows.push({ type: "mic1_snr", station: s, model: m, date: d, value: parseFloat(td.mic1ch0snr), status: entry.s });
          if (td.mic2ch0snr != null) rows.push({ type: "mic2_snr", station: s, model: m, date: d, value: parseFloat(td.mic2ch0snr), status: entry.s });
          if (td.mic0ch0thd != null) rows.push({ type: "mic0_thd", station: s, model: m, date: d, value: parseFloat(td.mic0ch0thd), status: entry.s });
        }
      });
    });
    return rows;
  }, [data]);

  const chartData = useMemo(() => {
    const filtered = parsed.filter(r => r.type === metric && !isNaN(r.value));
    if (!filtered.length) return [];
    const byStation = {};
    filtered.forEach(r => {
      if (!byStation[r.station]) byStation[r.station] = { station: r.station, vals: [], fails: 0, total: 0 };
      byStation[r.station].vals.push(r.value); byStation[r.station].total++;
      if (r.status === "FAIL") byStation[r.station].fails++;
    });
    return Object.values(byStation).map(s => ({
      station: s.station, avg: +avgA(s.vals).toFixed(3), min: +Math.min(...s.vals).toFixed(3),
      max: +Math.max(...s.vals).toFixed(3), std: +stdA(s.vals).toFixed(3),
      failRate: pctN(s.fails, s.total), n: s.total
    }));
  }, [parsed, metric]);

  const timeData = useMemo(() => {
    const filtered = parsed.filter(r => r.type === metric && r.date && !isNaN(r.value));
    const byDate = {};
    filtered.forEach(r => { if (!byDate[r.date]) byDate[r.date] = { date: r.date, vals: [] }; byDate[r.date].vals.push(r.value); });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({ date: d.date, avg: +avgA(d.vals).toFixed(3) }));
  }, [parsed, metric]);

  const noData = parsed.filter(r => r.type === metric).length === 0;

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
        {AQA_METRICS.map(m => (
          <button key={m.key} onClick={() => setMetric(m.key)} style={{
            padding: "5px 14px", borderRadius: 20, fontSize: 11, cursor: "pointer",
            border: `1px solid ${metric === m.key ? T.accent : T.border}`,
            background: metric === m.key ? T.accentBg : T.card, color: metric === m.key ? T.accent : T.textMid, fontWeight: metric === m.key ? 700 : 400
          }}>{m.label}</button>
        ))}
      </div>
      {noData ? (
        <EmptyState msg={`No ${cur.label} data found. Requires the logFile column in your export.`} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Card>
              <ChartTitle>Avg {cur.label} ({cur.unit}) by Station</ChartTitle>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="station" tick={{ fill: T.textMid, fontSize: 11 }} /><YAxis tick={{ fill: T.textMid, fontSize: 11 }} />
                  <Tooltip contentStyle={TS} />
                  <Bar dataKey="avg" fill={T.accent} radius={[4, 4, 0, 0]} name={`Avg ${cur.unit}`} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <ChartTitle>Fail Rate by Station (%)</ChartTitle>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="station" tick={{ fill: T.textMid, fontSize: 11 }} /><YAxis tick={{ fill: T.textMid, fontSize: 11 }} unit="%" />
                  <Tooltip contentStyle={TS} formatter={v => [v + "%", "Fail rate"]} />
                  <Bar dataKey="failRate" fill={T.fail} radius={[4, 4, 0, 0]} name="Fail rate %" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          {timeData.length > 1 && (
            <Card>
              <ChartTitle>Avg {cur.label} Over Time</ChartTitle>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={timeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="date" tick={{ fill: T.textMid, fontSize: 10 }} /><YAxis tick={{ fill: T.textMid, fontSize: 10 }} />
                  <Tooltip contentStyle={TS} /><Line type="monotone" dataKey="avg" stroke={T.accent} strokeWidth={2} dot={{ r: 3 }} name="Avg" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}
          <DataTable columns={[
            { key: "station", label: "Station" }, { key: "n", label: "N", right: true },
            { key: "avg", label: `Avg ${cur.unit}`, right: true }, { key: "min", label: "Min", right: true },
            { key: "max", label: "Max", right: true }, { key: "std", label: "Std Dev", right: true }, { key: "failRate", label: "Fail %", right: true }
          ]} rows={chartData} />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: REPEAT DEVICES
// ══════════════════════════════════════════════════
function RepeatDevicesTab({ data }) {
  const [minCount, setMinCount] = useState(2);
  const [selected, setSelected] = useState(null);

  const repeatData = useMemo(() => {
    const byId = {};
    data.forEach(row => {
      const id = row["IMEI"] || row["Serial Number"] || ""; if (!id) return;
      if (!byId[id]) byId[id] = { id, model: row["Model"] || "", manufacturer: row["Manufacturer"] || "", rows: [] };
      byId[id].rows.push(row);
    });
    return Object.values(byId).filter(d => d.rows.length >= minCount).map(d => {
      const failures = d.rows.filter(r => r["Overall status"] === "FAILURE").length;
      const dates = d.rows.map(r => r._dateStr).filter(Boolean).sort();
      return {
        ...d, count: d.rows.length, failures, failRate: pct(failures, d.rows.length),
        firstSeen: dates[0] || "—", lastSeen: dates.slice(-1)[0] || "—",
        stations: [...new Set(d.rows.map(r => r["Station ID"] || "Unknown"))].join(", ")
      };
    }).sort((a, b) => b.count - a.count);
  }, [data, minCount]);

  const deviceHistory = selected ? repeatData.find(d => d.id === selected) : null;

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 16, background: T.card,
        border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,.04)"
      }}>
        <span style={{ color: T.textMid, fontSize: 12, fontWeight: 600 }}>Min occurrences:</span>
        <input type="range" min={2} max={10} value={minCount} onChange={e => setMinCount(+e.target.value)} style={{ accentColor: T.accent, width: 100 }} />
        <span style={{ color: T.accent, fontWeight: 800, fontSize: 16, minWidth: 20 }}>{minCount}</span>
        <span style={{ color: T.textLight, fontSize: 11, marginLeft: "auto" }}>{repeatData.length} devices found</span>
        {selected && <button onClick={() => setSelected(null)} style={{
          background: T.failBg, border: `1px solid ${T.fail}33`, color: T.fail,
          borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11,
          display: "flex", alignItems: "center", gap: 5
        }}><AppIcon icon="✕" size={10} /> Close history</button>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 1fr" : "1fr", gap: 16 }}>
        <DataTable columns={[
          { key: "id", label: "IMEI / Serial", mono: true }, { key: "model", label: "Model" },
          { key: "count", label: "Count", right: true }, { key: "failures", label: "Failures", right: true },
          { key: "failRate", label: "Fail %", right: true }, { key: "stations", label: "Stations" },
          { key: "firstSeen", label: "First" }, { key: "lastSeen", label: "Last" }
        ]} rows={repeatData} maxRows={100} onRowClick={row => setSelected(row.id === selected ? null : row.id)} />
        {deviceHistory && (
          <Card>
            <div style={{ fontWeight: 700, color: T.text, marginBottom: 12, fontSize: 13 }}>
              {deviceHistory.model} — <span style={{ fontFamily: "monospace", fontSize: 11 }}>{deviceHistory.id}</span>
            </div>
            <DataTable columns={[
              { key: "_dateStr", label: "Date" }, { key: "Station ID", label: "Station" },
              { key: "Overall status", label: "Status", badge: true }, { key: "result_FAIL", label: "Fail Reason" }
            ]} rows={deviceHistory.rows} maxRows={50} />
          </Card>
        )}
      </div>
      {repeatData.length === 0 && <EmptyState msg={`No devices found with ${minCount}+ occurrences`} />}
      {!selected && repeatData.length > 0 && <div style={{ marginTop: 8, fontSize: 11, color: T.textLight, display: "flex", alignItems: "center", gap: 6 }}><AppIcon icon="👆" size={11} /> Click a row to view the device's full test history</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: TIME PATTERNS
// ══════════════════════════════════════════════════
function TimePatternsTab({ data }) {
  const getHour = row => {
    if (row["Hour"] !== undefined && row["Hour"] !== "") return parseInt(row["Hour"]);
    if (row._date && !isNaN(row._date)) return row._date.getHours();
    return null;
  };

  const hourData = useMemo(() => {
    const bh = {};
    data.forEach(row => {
      const h = getHour(row); if (h === null || isNaN(h)) return;
      if (!bh[h]) bh[h] = { hour: h, label: `${h}h`, total: 0, fail: 0, devices: new Set() };
      bh[h].total++;
      if (row["Overall status"] === "FAILURE") bh[h].fail++;
      const devId = row["IMEI"] || row["Serial Number"] || ""; if (devId) bh[h].devices.add(devId);
    });
    return Array.from({ length: 24 }, (_, h) => ({
      ...(bh[h] || { hour: h, label: `${h}h`, total: 0, fail: 0 }),
      deviceCount: bh[h] ? bh[h].devices.size : 0,
      failPct: pctN((bh[h]?.fail || 0), (bh[h]?.total || 0))
    }));
  }, [data]);

  const dowData = useMemo(() => {
    const bd = {};
    data.forEach(row => {
      if (!row._date || isNaN(row._date)) return;
      const d = row._date.getDay();
      if (!bd[d]) bd[d] = { dow: d, label: DOW[d], total: 0, fail: 0 };
      bd[d].total++; if (row["Overall status"] === "FAILURE") bd[d].fail++;
    });
    return Array.from({ length: 7 }, (_, d) => ({ ...(bd[d] || { dow: d, label: DOW[d], total: 0, fail: 0 }), failPct: pctN((bd[d]?.fail || 0), (bd[d]?.total || 0)) }));
  }, [data]);

  const stationHourData = useMemo(() => {
    const bhs = {};
    data.forEach(row => {
      const h = getHour(row); if (h === null || isNaN(h)) return;
      const s = row["Station ID"] || "Unknown";
      const key = `${h}::${s}`;
      if (!bhs[key]) bhs[key] = { hour: h, station: s, count: 0, fail: 0 };
      bhs[key].count++; if (row["Overall status"] === "FAILURE") bhs[key].fail++;
    });
    return Object.values(bhs);
  }, [data]);

  const stationsInHour = [...new Set(stationHourData.map(c => c.station))].sort();
  const hasHour = hourData.some(d => d.total > 0);
  const hasDate = dowData.some(d => d.total > 0);

  if (!hasHour && !hasDate) return <EmptyState msg="No valid date/hour data found. Ensure Date or Hour columns are present." />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {hasHour && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card>
            <ChartTitle>Device Number by Hour</ChartTitle>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={hourData}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis dataKey="label" tick={{ fill: T.textMid, fontSize: 9 }} interval={1} /><YAxis tick={{ fill: T.textMid, fontSize: 10 }} />
                <Tooltip contentStyle={TS} formatter={v => [v, "Unique devices"]} /><Bar dataKey="deviceCount" fill={T.accent} radius={[3, 3, 0, 0]} name="Devices" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <ChartTitle>Fail Rate by Hour (%)</ChartTitle>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={hourData.filter(d => d.total > 0)}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis dataKey="label" tick={{ fill: T.textMid, fontSize: 9 }} /><YAxis tick={{ fill: T.textMid, fontSize: 10 }} unit="%" />
                <Tooltip contentStyle={TS} formatter={v => [v + "%", "Fail rate"]} />
                <Bar dataKey="failPct" radius={[3, 3, 0, 0]} name="Fail %">
                  {hourData.filter(d => d.total > 0).map((e, i) => <Cell key={i} fill={e.failPct > 40 ? T.fail : e.failPct > 20 ? T.warn : T.pass} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}
      {hasDate && (
        <Card>
          <ChartTitle>Fail Rate by Day of Week</ChartTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dowData.filter(d => d.total > 0)}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="label" tick={{ fill: T.textMid, fontSize: 11 }} /><YAxis tick={{ fill: T.textMid, fontSize: 10 }} unit="%" />
              <Tooltip contentStyle={TS} formatter={v => [v + "%", "Fail rate"]} />
              <Bar dataKey="failPct" radius={[4, 4, 0, 0]} name="Fail %">
                {dowData.filter(d => d.total > 0).map((e, i) => <Cell key={i} fill={e.failPct > 40 ? T.fail : e.failPct > 20 ? T.warn : T.accent} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
      {hasHour && stationsInHour.length > 0 && (
        <Card noPad>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, fontWeight: 700, color: T.text, fontSize: 13 }}>
            Hourly Fail Rate Heatmap — by Station
          </div>
          <div style={{ overflowX: "auto", padding: 12 }}>
            <table style={{ borderCollapse: "separate", borderSpacing: 3, fontSize: 10 }}>
              <thead><tr>
                <th style={{ padding: "4px 8px", color: T.textLight, textAlign: "left", minWidth: 80 }}>Station</th>
                {Array.from({ length: 24 }, (_, h) => <th key={h} style={{ padding: "3px 5px", color: T.textMid, textAlign: "center", fontWeight: 600 }}>{h}h</th>)}
              </tr></thead>
              <tbody>
                {stationsInHour.map(station => (
                  <tr key={station}>
                    <td style={{ padding: "4px 8px", color: T.accent, fontWeight: 700, whiteSpace: "nowrap" }}>{station}</td>
                    {Array.from({ length: 24 }, (_, h) => {
                      const cell = stationHourData.find(c => c.hour === h && c.station === station);
                      if (!cell || cell.count === 0) return <td key={h} style={{ background: "#f1f5f9", borderRadius: 3, width: 28, height: 22 }} />;
                      const rate = pctN(cell.fail, cell.count);
                      return (
                        <td key={h} title={`${station} ${h}h: ${rate}% (${cell.fail}/${cell.count})`}
                          style={{ background: hBg(rate), borderRadius: 3, width: 28, height: 22, textAlign: "center" }}>
                          {rate > 0 && <span style={{ color: hFg(rate), fontSize: 8, fontWeight: 700 }}>{Math.round(rate)}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: SKIP RATE
// ══════════════════════════════════════════════════
function SkipRateTab({ data }) {
  const [view, setView] = useState("heatmap");
  const ftCols = useMemo(() => computeFtCols(data), [data]);
  const stations = useMemo(() => [...new Set(data.map(r => r["Station ID"] || "Unknown").filter(Boolean))].sort(), [data]);

  const skipData = useMemo(() => {
    const cells = {};
    stations.forEach(s => { cells[s] = {}; ftCols.forEach(col => { cells[s][col] = { skip: 0, total: 0 }; }); });
    data.forEach(row => {
      const s = row["Station ID"] || "Unknown";
      ftCols.forEach(col => { const v = row[col]; if (!v || v === "") return; const c = cells[s]?.[col]; if (!c) return; c.total++; if (v === "SKIP") c.skip++; });
    });
    // top skipped tests
    const totals = {};
    ftCols.forEach(col => {
      let sk = 0, t = 0;
      stations.forEach(s => { const c = cells[s]?.[col]; if (c) { sk += c.skip; t += c.total; } });
      totals[col] = { col: col.replace("FT_", ""), skip: sk, total: t, skipPct: pctN(sk, t) };
    });
    return { cells, topSkipped: Object.values(totals).sort((a, b) => b.skip - a.skip) };
  }, [data, stations, ftCols]);

  return (
    <div>
      <SubTab tabs={[
        ["heatmap", <><AppIcon icon="🗺" size={12} /> Heatmap</>],
        ["table", <><AppIcon icon="📋" size={12} /> Table</>],
      ]} active={view} onSelect={setView} />

      {view === "heatmap" && (
        <Card noPad>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, fontWeight: 700, color: T.text }}>
            Skip Rate — Station × Test (%)
          </div>
          <div style={{ overflowX: "auto", padding: 12 }}>
            <table style={{ borderCollapse: "separate", borderSpacing: 3, fontSize: 10 }}>
              <thead><tr>
                <th style={{ padding: "4px 8px", color: T.textLight, textAlign: "left", minWidth: 80 }}>Station</th>
                {ftCols.map(col => (
                  <th key={col} style={{
                    padding: "2px 4px", color: T.textMid, fontWeight: 600, textAlign: "center",
                    writingMode: "vertical-rl", transform: "rotate(180deg)", height: 90, whiteSpace: "nowrap", verticalAlign: "bottom"
                  }}>
                    {col.replace("FT_", "")}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {stations.map(s => (
                  <tr key={s}>
                    <td style={{ padding: "4px 8px", color: T.accent, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>{s}</td>
                    {ftCols.map(col => {
                      const c = skipData.cells[s]?.[col];
                      if (!c || c.total === 0) return <td key={col} style={{ background: "#f1f5f9", borderRadius: 4, width: 26, height: 22 }} />;
                      const rate = pctN(c.skip, c.total);
                      const bg = rate === 0 ? "#f0fdf4" : rate < 30 ? "#fffbeb" : rate < 70 ? "#fff7ed" : "#fef2f2";
                      const fg = rate === 0 ? "#166534" : rate < 30 ? "#854d0e" : rate < 70 ? "#9a3412" : "#991b1b";
                      return (
                        <td key={col} title={`${s} / ${col.replace("FT_", "")}: ${rate}% skip (${c.skip}/${c.total})`}
                          style={{ background: bg, borderRadius: 4, width: 26, height: 22, textAlign: "center" }}>
                          {rate > 0 && <span style={{ color: fg, fontSize: 9, fontWeight: 700 }}>{Math.round(rate)}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {view === "table" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <SectionTitle sub="Tests with most skips across all stations">Top Skipped Tests</SectionTitle>
            <DataTable columns={[
              { key: "col", label: "Test Component" },
              { key: "skip", label: "Total Skips", right: true },
              { key: "total", label: "Total Tested", right: true },
              { key: "skipPct", label: "Skip %", right: true },
            ]} rows={skipData.topSkipped.filter(r => r.skip > 0).map(r => ({ ...r, skipPct: r.skipPct.toFixed(1) + "%" }))} />
          </div>
          <div>
            <SectionTitle sub="Per station skip breakdown">Station Skip Summary</SectionTitle>
            <DataTable columns={[
              { key: "station", label: "Station" },
              { key: "totalSkips", label: "Total Skips", right: true },
              { key: "totalTests", label: "Total Tested", right: true },
              { key: "skipPct", label: "Skip %", right: true },
            ]} rows={stations.map(s => {
              let sk = 0, t = 0;
              ftCols.forEach(col => { const c = skipData.cells[s]?.[col]; if (c) { sk += c.skip; t += c.total; } });
              return { station: s, totalSkips: sk, totalTests: t, skipPct: pctN(sk, t).toFixed(1) + "%" };
            })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TAB: TEST DURATION
// ══════════════════════════════════════════════════
function TestDurationTab({ data }) {
  const [view, setView] = useState("trend");
  const [compareVer, setCompareVer] = useState(false);

  // Parse Total test time (ms) — column name from CSV
  const getTTT = row => {
    const v = row["Total test time"] || row["total test time"] || row["Total Test Time"] || "";
    const n = parseFloat(String(v).replace(/,/g, ""));
    return isNaN(n) || n <= 0 ? null : n;
  };

  const validRows = useMemo(() => data.filter(r => getTTT(r) !== null).map(r => ({
    ...r, _ttt: getTTT(r)
  })), [data]);

  // Global KPIs
  const kpis = useMemo(() => {
    if (!validRows.length) return null;
    const vals = validRows.map(r => r._ttt).sort((a, b) => a - b);
    const sum = vals.reduce((a, b) => a + b, 0);
    const avg = sum / vals.length;
    const med = vals.length % 2 === 0 ? (vals[vals.length / 2 - 1] + vals[vals.length / 2]) / 2 : vals[Math.floor(vals.length / 2)];
    return { n: vals.length, avg, min: vals[0], max: vals[vals.length - 1], med, std: stdA(vals) };
  }, [validRows]);

  // Trend: avg per day
  const trendData = useMemo(() => {
    const bd = {};
    validRows.forEach(r => {
      const d = r._dateStr; if (!d) return;
      if (!bd[d]) bd[d] = { date: d, vals: [], fail: 0, total: 0 };
      bd[d].vals.push(r._ttt); bd[d].total++;
      if (r["Overall status"] === "FAILURE") bd[d].fail++;
    });
    return Object.values(bd).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({
      date: d.date,
      avgMin: +(avgA(d.vals) / 60000).toFixed(2),
      minMin: +(Math.min(...d.vals) / 60000).toFixed(2),
      maxMin: +(Math.max(...d.vals) / 60000).toFixed(2),
      n: d.total
    }));
  }, [validRows]);

  // Per station
  const stationData = useMemo(() => {
    const bs = {};
    validRows.forEach(r => {
      const s = r["Station ID"] || "Unknown";
      if (!bs[s]) bs[s] = { station: s, vals: [] };
      bs[s].vals.push(r._ttt);
    });
    return Object.values(bs).map(s => ({
      station: s.station, n: s.vals.length,
      avgMin: +(avgA(s.vals) / 60000).toFixed(2),
      minMin: +(Math.min(...s.vals) / 60000).toFixed(2),
      maxMin: +(Math.max(...s.vals) / 60000).toFixed(2),
      stdMin: +(stdA(s.vals) / 60000).toFixed(2)
    })).sort((a, b) => b.avgMin - a.avgMin);
  }, [validRows]);

  // Per model (top 15)
  const modelData = useMemo(() => {
    const bm = {};
    validRows.forEach(r => {
      const m = r["Model"] || "Unknown";
      if (!bm[m]) bm[m] = { model: m, vals: [] };
      bm[m].vals.push(r._ttt);
    });
    return Object.values(bm).map(m => ({
      model: m.model, n: m.vals.length,
      avgMin: +(avgA(m.vals) / 60000).toFixed(2),
      minMin: +(Math.min(...m.vals) / 60000).toFixed(2),
      maxMin: +(Math.max(...m.vals) / 60000).toFixed(2),
      stdMin: +(stdA(m.vals) / 60000).toFixed(2)
    })).sort((a, b) => b.avgMin - a.avgMin).slice(0, 15);
  }, [validRows]);

  // Distribution histogram (buckets of 1 min)
  const histData = useMemo(() => {
    if (!validRows.length) return [];
    const buckets = {};
    validRows.forEach(r => {
      const m = Math.floor(r._ttt / 60000);
      const label = `${m}-${m + 1}min`;
      if (!buckets[label]) buckets[label] = { label, range: m, count: 0 };
      buckets[label].count++;
    });
    return Object.values(buckets).sort((a, b) => a.range - b.range);
  }, [validRows]);

  // App versions available
  const appVersions = useMemo(() => [...new Set(validRows.map(r => r["App version"]).filter(Boolean))].sort(), [validRows]);

  // Version comparison trend data: {date, ver1: avgMin, ver2: avgMin, ...}
  const verTrendData = useMemo(() => {
    const allDates = [...new Set(validRows.map(r => r._dateStr).filter(Boolean))].sort();
    return allDates.map(date => {
      const row = { date };
      appVersions.forEach(ver => {
        const vals = validRows.filter(r => r._dateStr === date && r["App version"] === ver).map(r => r._ttt);
        row[ver] = vals.length ? +(avgA(vals) / 60000).toFixed(2) : null;
      });
      return row;
    });
  }, [validRows, appVersions]);

  const fmtMin = v => fmtDuration(v);

  if (!validRows.length) return <EmptyState msg="No 'Total test time' data found. Ensure the column exists in your export." />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <KpiCard label="Avg Duration" value={fmtDuration(kpis.avg / 60000)} color={T.accent} icon="⏱" sub={`${kpis.n} tests`} />
        <KpiCard label="Median" value={fmtDuration(kpis.med / 60000)} color={T.purple} icon="📊" />
        <KpiCard label="Min" value={fmtDuration(kpis.min / 60000)} color={T.pass} icon="⬇" />
        <KpiCard label="Max" value={fmtDuration(kpis.max / 60000)} color={T.fail} icon="⬆" />
        <KpiCard label="Std Dev" value={fmtDuration(kpis.std / 60000)} color={T.warn} icon="±" />
      </div>
      <SubTab tabs={[
        ["trend", <><AppIcon icon="📈" size={12} /> Daily Trend</>],
        ["station", <><AppIcon icon="🏭" size={12} /> By Station</>],
        ["model", <><AppIcon icon="📱" size={12} /> By Model</>],
        ["dist", <><AppIcon icon="📊" size={12} /> Distribution</>],
      ]} active={view} onSelect={setView} />
      {view === "trend" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setCompareVer(v => !v)} style={{
              padding: "5px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer",
              border: `1px solid ${compareVer ? T.accent : T.border}`, background: compareVer ? T.accentBg : T.card,
              color: compareVer ? T.accent : T.textMid, fontWeight: compareVer ? 700 : 400,
              display: "flex", alignItems: "center", gap: 6
            }}>
              {compareVer && <AppIcon icon="✓" size={11} strokeWidth={3} />}
              <AppIcon icon="🔄" size={12} /> Compare by App Version
            </button>
            {compareVer && <span style={{ color: T.textLight, fontSize: 11 }}>{appVersions.length} version(s) detected</span>}
          </div>
          <Card>
            <ChartTitle>Avg Total Test Time per Day (minutes)</ChartTitle>
            {(compareVer ? verTrendData : trendData).length < 2 ? <EmptyState msg="Need 2+ date points to show trend" /> : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={compareVer ? verTrendData : trendData} margin={{ top: 5, right: 24, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="date" tick={{ fill: T.textMid, fontSize: 10 }} />
                  <YAxis tick={{ fill: T.textMid, fontSize: 10 }} unit="min" />
                  <Tooltip contentStyle={TS} formatter={(v, n) => [`${v}min`, n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {compareVer ? appVersions.map((ver, i) => (
                    <Line key={ver} type="monotone" dataKey={ver} stroke={SC[i % SC.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} name={ver} />
                  )) : (
                    <>
                      <Line type="monotone" dataKey="avgMin" stroke={T.accent} strokeWidth={2.5} dot={{ r: 4 }} name="Avg (min)" />
                      <Line type="monotone" dataKey="maxMin" stroke={T.fail} strokeWidth={1.5} strokeDasharray="5 3" dot={false} name="Max (min)" />
                      <Line type="monotone" dataKey="minMin" stroke={T.pass} strokeWidth={1.5} strokeDasharray="5 3" dot={false} name="Min (min)" />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}
      {view === "station" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ padding: "14px 8px" }}>
            <ChartTitle>Avg Test Duration by Station (minutes)</ChartTitle>
            <ResponsiveContainer width="100%" height={Math.max(stationData.length * 44, 160)}>
              <BarChart data={stationData} layout="vertical" margin={{ left: 100, right: 60, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis type="number" tick={{ fill: T.textMid, fontSize: 10 }} unit="min" />
                <YAxis type="category" dataKey="station" tick={{ fill: T.text, fontSize: 11 }} width={96} />
                <Tooltip contentStyle={TS} formatter={(v, n) => [`${v}min`, n]} />
                <Bar dataKey="avgMin" name="Avg (min)" radius={[0, 4, 4, 0]}>
                  {stationData.map((e, i) => <Cell key={i} fill={SC[i % SC.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <DataTable columns={[
            { key: "station", label: "Station" }, { key: "n", label: "N", right: true },
            { key: "avgMin", label: "Avg (min)", right: true }, { key: "minMin", label: "Min (min)", right: true },
            { key: "maxMin", label: "Max (min)", right: true }, { key: "stdMin", label: "Std Dev (min)", right: true }
          ]} rows={stationData.map(r => ({ ...r, avgMin: fmtMin(r.avgMin), minMin: fmtMin(r.minMin), maxMin: fmtMin(r.maxMin), stdMin: fmtMin(r.stdMin) }))} />
        </div>
      )}
      {view === "model" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ padding: "14px 8px" }}>
            <ChartTitle>Avg Test Duration by Model — Top 15 (minutes)</ChartTitle>
            <ResponsiveContainer width="100%" height={Math.max(modelData.length * 40, 160)}>
              <BarChart data={modelData} layout="vertical" margin={{ left: 120, right: 60, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis type="number" tick={{ fill: T.textMid, fontSize: 10 }} unit="min" />
                <YAxis type="category" dataKey="model" tick={{ fill: T.text, fontSize: 11 }} width={114} />
                <Tooltip contentStyle={TS} formatter={(v, n) => [`${v}min`, n]} />
                <Bar dataKey="avgMin" name="Avg (min)" radius={[0, 4, 4, 0]}>
                  {modelData.map((e, i) => <Cell key={i} fill={SC[i % SC.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <DataTable columns={[
            { key: "model", label: "Model" }, { key: "n", label: "N", right: true },
            { key: "avgMin", label: "Avg (min)", right: true }, { key: "minMin", label: "Min (min)", right: true },
            { key: "maxMin", label: "Max (min)", right: true }, { key: "stdMin", label: "Std Dev (min)", right: true }
          ]} rows={modelData.map(r => ({ ...r, avgMin: fmtMin(r.avgMin), minMin: fmtMin(r.minMin), maxMin: fmtMin(r.maxMin), stdMin: fmtMin(r.stdMin) }))} />
        </div>
      )}
      {view === "dist" && (
        <Card>
          <ChartTitle>Duration Distribution — buckets of 1 min</ChartTitle>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={histData} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="label" tick={{ fill: T.textMid, fontSize: 10, angle: -40, textAnchor: "end" }} interval={0} />
              <YAxis tick={{ fill: T.textMid, fontSize: 10 }} />
              <Tooltip contentStyle={TS} formatter={(v) => [v, "Tests"]} />
              <Bar dataKey="count" name="Tests" radius={[4, 4, 0, 0]}>
                {histData.map((e, i) => <Cell key={i} fill={SC[i % SC.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// PDF REPORT GENERATOR
// ══════════════════════════════════════════════════
function generatePDFReport(filtered, selectedCharts, opts = {}) {
  const ftCols = computeFtCols(filtered);
  const total = filtered.length;
  const failures = filtered.filter(r => r["Overall status"] === "FAILURE").length;
  const passes = filtered.filter(r => r["Overall status"] === "SUCCESS").length;
  const failPct = total ? ((failures / total) * 100).toFixed(1) : 0;
  const models = new Set(filtered.map(r => r["Model"]).filter(Boolean)).size;
  const stations = new Set(filtered.map(r => r["Station ID"]).filter(Boolean)).size;
  const dates = filtered.map(r => r._dateStr).filter(Boolean).sort();
  const period = dates.length ? (dates[0] === dates[dates.length - 1] ? dates[0] : `${dates[0]} → ${dates[dates.length - 1]}`) : "—";

  // Top 5 failures — split result_FAIL by comma to count individual tests
  const failReasons = {};
  filtered.forEach(r => {
    const f = r["result_FAIL"] || "";
    if (!f) return;
    f.split(",").forEach(v => { const name = v.trim(); if (name) failReasons[name] = (failReasons[name] || 0) + 1; });
  });
  const top5 = Object.entries(failReasons).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Fail by model
  const byModel = {};
  filtered.forEach(r => {
    const m = r["Model"] || "Unknown";
    if (!byModel[m]) byModel[m] = { fail: 0, total: 0 };
    byModel[m].total++;
    if (r["Overall status"] === "FAILURE") byModel[m].fail++;
  });
  const modelRows = Object.entries(byModel).map(([m, v]) => ({ model: m, fail: v.fail, total: v.total, pct: v.total ? (v.fail / v.total * 100).toFixed(1) : 0 })).sort((a, b) => b.fail - a.fail).slice(0, 15);

  // Chart 1: Trend SVG
  const trendMap = {};
  filtered.forEach(r => { const d = r._dateStr; if (!d) return; if (!trendMap[d]) trendMap[d] = { f: 0, t: 0 }; trendMap[d].t++; if (r["Overall status"] === "FAILURE") trendMap[d].f++; });
  const td = Object.entries(trendMap).sort(([a], [b]) => a.localeCompare(b)).map(([date, v]) => ({ date, fp: v.t ? +(v.f / v.t * 100).toFixed(1) : 0 }));
  function svgTrend() {
    if (td.length < 2) return '<p style="color:#888">Insufficient data (2+ days required)</p>';
    const W = 660, H = 180, PL = 42, PR = 16, PT = 10, PB = 36;
    const iW = W - PL - PR, iH = H - PT - PB;
    const maxY = Math.max(...td.map(d => d.fp), 5);
    const x = i => PL + (i / (td.length - 1)) * iW;
    const y = v => PT + iH - (v / maxY) * iH;
    const pts = td.map((d, i) => `${x(i)},${y(d.fp)}`).join(" ");
    const step = Math.ceil(td.length / 10);
    return `<svg width="${W}" height="${H}" style="font-family:sans-serif;overflow:visible">
      ${[0, 25, 50, 75, 100].filter(v => v <= maxY + 10).map(v => `<line x1="${PL}" y1="${y(v)}" x2="${PL + iW}" y2="${y(v)}" stroke="#f1f5f9"/><text x="${PL - 5}" y="${y(v) + 3}" text-anchor="end" font-size="9" fill="#94a3b8">${v}%</text>`).join("")}
      <line x1="${PL}" y1="${PT}" x2="${PL}" y2="${PT + iH}" stroke="#e2e8f0"/>
      <line x1="${PL}" y1="${PT + iH}" x2="${PL + iW}" y2="${PT + iH}" stroke="#e2e8f0"/>
      <polyline points="${pts}" fill="none" stroke="#ef4444" stroke-width="2"/>
      ${td.map((d, i) => `<circle cx="${x(i)}" cy="${y(d.fp)}" r="3" fill="#ef4444"/>`).join("")}
      ${td.filter((_, i) => i % step === 0 || i === td.length - 1).map((d, _, arr) => { const i = td.indexOf(d); return `<text x="${x(i)}" y="${PT + iH + 14}" text-anchor="middle" font-size="8" fill="#64748b" transform="rotate(-30 ${x(i)} ${PT + iH + 14})">${d.date.slice(5)}</text>`; }).join("")}
    </svg>`;
  }

  // Chart 2: Station bars HTML
  const stMap = {};
  filtered.forEach(r => { const s = r["Station ID"] || "Unknown"; if (!stMap[s]) stMap[s] = 0; if (r["Overall status"] === "FAILURE") stMap[s]++; });
  const stData = Object.entries(stMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
  function htmlStation() {
    if (!stData.length) return '<p style="color:#888">No data</p>';
    const mx = stData[0][1] || 1;
    return stData.map(([n, v]) => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px"><div style="min-width:110px;font-size:11px;color:#334155;text-align:right;white-space:nowrap">${n}</div><div style="flex:1;background:#f1f5f9;height:18px;border-radius:3px"><div style="width:${(v/mx*100).toFixed(1)}%;height:100%;background:#ef4444;border-radius:3px"></div></div><div style="min-width:28px;font-size:11px;font-weight:700;color:#ef4444">${v}</div></div>`).join("");
  }

  // Chart 3: Pie SVG
  function svgPie() {
    const tot = passes + failures; if (!tot) return '<p style="color:#888">No data</p>';
    const fA = failures / tot * 360, pA = 360 - fA;
    function arc(s, e, color, label, cnt) {
      if (cnt === 0) return "";
      if (cnt === tot) return `<circle cx="100" cy="100" r="80" fill="${color}"/><text x="100" y="105" text-anchor="middle" font-size="14" font-weight="bold" fill="#fff">${label}: ${cnt}</text>`;
      const r = 80, [s1, e1] = [(s - 90) * Math.PI / 180, (e - 90) * Math.PI / 180];
      const lg = e - s > 180 ? 1 : 0;
      const d = `M100,100 L${100 + r * Math.cos(s1)},${100 + r * Math.sin(s1)} A${r},${r} 0 ${lg},1 ${100 + r * Math.cos(e1)},${100 + r * Math.sin(e1)} Z`;
      const mid = ((s + e) / 2 - 90) * Math.PI / 180;
      return `<path d="${d}" fill="${color}"/><text x="${100 + (r + 22) * Math.cos(mid)}" y="${100 + (r + 22) * Math.sin(mid)}" text-anchor="middle" font-size="11" font-weight="700" fill="#0f172a">${label}<tspan x="${100 + (r + 22) * Math.cos(mid)}" dy="12">${cnt}</tspan></text>`;
    }
    return `<svg width="260" height="220" style="font-family:sans-serif">${arc(0, fA, "#ef4444", "FAIL", failures)}${arc(fA, fA + pA, "#10b981", "PASS", passes)}<text x="100" y="210" text-anchor="middle" font-size="11" fill="#64748b">${failPct}% failure rate</text></svg>`;
  }

  // Chart 4: Heatmap HTML
  function htmlHeatmap() {
    const stList = [...new Set(filtered.map(r => r["Station ID"] || "Unknown"))].sort().slice(0, 8);
    const cols = ftCols.slice(0, 14);
    const cells = {};
    stList.forEach(s => { cells[s] = {}; cols.forEach(c => { cells[s][c] = { f: 0, t: 0 }; }); });
    filtered.forEach(row => { const s = row["Station ID"] || "Unknown"; if (!cells[s]) return; cols.forEach(c => { const v = row[c]; if (!v) return; cells[s][c].t++; if (v === "FAIL") cells[s][c].f++; }); });
    const hB = r => r === 0 ? "#f0fdf4" : r < 10 ? "#dcfce7" : r < 25 ? "#fefce8" : r < 50 ? "#fff7ed" : "#fef2f2";
    const hF = r => r === 0 ? "#166534" : r < 10 ? "#15803d" : r < 25 ? "#854d0e" : r < 50 ? "#9a3412" : "#991b1b";
    return `<table style="border-collapse:separate;border-spacing:2px;font-size:9px"><thead><tr><th style="padding:2px 8px;text-align:left;color:#64748b;min-width:80px">Station</th>${cols.map(c => `<th style="padding:2px 3px;writing-mode:vertical-rl;transform:rotate(180deg);height:55px;vertical-align:bottom;color:#475569;font-weight:600">${c.replace("FT_", "")}</th>`).join("")}</tr></thead><tbody>${stList.map(s => `<tr><td style="padding:2px 6px;color:#2563eb;font-weight:700;white-space:nowrap">${s}</td>${cols.map(c => { const cl = cells[s]?.[c]; if (!cl || cl.t === 0) return '<td style="background:#f1f5f9;width:22px;height:18px"></td>'; const rate = Math.round(cl.f / cl.t * 100); return `<td title="${rate}%" style="background:${hB(rate)};width:22px;height:18px;text-align:center;color:${hF(rate)};font-weight:700">${rate > 0 ? rate : ""}</td>`; }).join("")}</tr>`).join("")}</tbody></table>`;
  }

  // Chart 5: FT_ bars HTML
  const ftData = ftCols.map(c => ({ name: c.replace("FT_", ""), fails: filtered.filter(r => r[c] === "FAIL").length })).filter(r => r.fails > 0).sort((a, b) => b.fails - a.fails).slice(0, 14);
  function htmlFT() {
    if (!ftData.length) return '<p style="color:#888">No data</p>';
    const mx = ftData[0].fails;
    return ftData.map(({ name, fails }) => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px"><div style="min-width:130px;font-size:10px;color:#334155;text-align:right;white-space:nowrap">${name}</div><div style="flex:1;background:#f1f5f9;height:16px;border-radius:3px"><div style="width:${(fails/mx*100).toFixed(1)}%;height:100%;background:#f59e0b;border-radius:3px"></div></div><div style="min-width:28px;font-size:10px;font-weight:700;color:#b45309">${fails}</div></div>`).join("");
  }

  // Pass / Fail by Station
  const bySt = {};
  filtered.forEach(r => { const s = r["Station ID"] || "Unknown"; if(!bySt[s]) bySt[s] = { s, p:0, f:0, t:0 }; bySt[s].t++; if(r["Overall status"] === "SUCCESS") bySt[s].p++; if(r["Overall status"] === "FAILURE") bySt[s].f++; });
  const pfRows = Object.values(bySt).sort((a,b) => b.t - a.t);
  function htmlPassFail() {
    if(!pfRows.length) return '<p style="color:#888">No data</p>';
    return `<table class="s"><thead><tr><th>Station</th><th style="text-align:right">PASS</th><th style="text-align:right">FAIL</th><th style="text-align:right">Total</th><th style="text-align:right">% Fail</th></tr></thead><tbody>${pfRows.map(r => `<tr><td>${r.s}</td><td style="text-align:right;color:#10b981;font-weight:700">${r.p}</td><td style="text-align:right;color:#ef4444;font-weight:700">${r.f}</td><td style="text-align:right">${r.t}</td><td style="text-align:right;font-weight:700">${r.t ? (r.f/r.t*100).toFixed(1) : 0}%</td></tr>`).join("")}</tbody></table>`;
  }

  // Hourly Trend
  const hMap = {};
  filtered.forEach(r => { const h = r["Hour"] || r["Time"] || r["Start Time"]; if(h) { const h2 = String(h).substring(0,2); if(!isNaN(h2)) { if(!hMap[h2]) hMap[h2] = { h:h2, f:0, t:0 }; hMap[h2].t++; if(r["Overall status"] === "FAILURE") hMap[h2].f++; } } });
  const hRows = Object.values(hMap).sort((a,b) => a.h - b.h);
  function htmlHourly() {
    if(!hRows.length) return '<p style="color:#888">No data</p>';
    const mx = Math.max(...hRows.map(r => r.t));
    return hRows.map(({h,f,t}) => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px"><div style="min-width:40px;font-size:10px;color:#334155;text-align:right">${h}h</div><div style="flex:1;background:#f1f5f9;height:16px;border-radius:3px;display:flex;overflow:hidden"><div style="width:${(t/mx*100).toFixed(1)}%;height:100%;background:#3b82f6;display:flex"><div style="width:${t ? (f/t*100).toFixed(1) : 0}%;height:100%;background:#ef4444"></div></div></div><div style="min-width:30px;font-size:10px;text-align:right">${t}</div></div>`).join("");
  }

  // Test Duration by Station
  const dMap = {};
  filtered.forEach(r => { const st = r["Station ID"] || "Unknown"; const dur = r["Test Duration"] || r["Duration"]; if(dur) { const sec = Number(dur); if(!isNaN(sec)) { if(!dMap[st]) dMap[st] = []; dMap[st].push(sec); } } });
  const avg = arr => arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;
  const dRows = Object.entries(dMap).map(([s, arr]) => ({ s, a: avg(arr) })).sort((a,b)=>b.a - a.a).slice(0, 20);
  function htmlDuration() {
    if(!dRows.length) return '<p style="color:#888">No data</p>';
    const mx = Math.max(...dRows.map(r => r.a));
    return dRows.map(({s,a}) => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px"><div style="min-width:110px;font-size:10px;color:#334155;text-align:right;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${s}</div><div style="flex:1;background:#f1f5f9;height:16px;border-radius:3px"><div style="width:${(a/mx*100).toFixed(1)}%;height:100%;background:#8b5cf6;border-radius:3px"></div></div><div style="min-width:30px;font-size:10px;font-weight:700;text-align:right">${fmtDuration(a / 60)}</div></div>`).join("");
  }

  // Idle Time by Station
  const toSec = t => { if (!t) return null; const p = String(t).split(":"); return p.length === 3 ? +p[0] * 3600 + +p[1] * 60 + +p[2] : null; };
  const iMap = {};
  filtered.forEach(r => { const st = r["Station ID"] || "Unknown"; const dt = r._dateStr || ""; const sec = toSec(r["Hour"] || r["Time"] || r["Start Time"] || ""); if (!dt || sec === null) return; const key = st + "||" + dt; if (!iMap[key]) iMap[key] = { st, rows: [] }; iMap[key].rows.push(sec); });
  const sMap = {};
  Object.values(iMap).forEach(({st, rows}) => { rows.sort((a,b)=>a-b); for(let i=1; i<rows.length; i++) { const g = rows[i] - rows[i-1]; if(g <= 0 || g > 900) continue; if(!sMap[st]) sMap[st] = []; sMap[st].push(g/60); } });
  const iRows = Object.entries(sMap).map(([s, arr]) => ({ s, a: avg(arr) })).sort((a,b)=>b.a - a.a).slice(0, 20);
  function htmlIdle() {
    if(!iRows.length) return '<p style="color:#888">No data</p>';
    const mx = Math.max(...iRows.map(r => r.a), 1);
    const c = v => v < 2 ? '#10b981' : v < 5 ? '#f59e0b' : '#ef4444';
    return iRows.map(({s,a}) => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px"><div style="min-width:110px;font-size:10px;color:#334155;text-align:right;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${s}</div><div style="flex:1;background:#f1f5f9;height:16px;border-radius:3px"><div style="width:${(a/mx*100).toFixed(1)}%;height:100%;background:${c(a)};border-radius:3px"></div></div><div style="min-width:30px;font-size:10px;font-weight:700;color:${c(a)};text-align:right">${fmtDuration(a)}</div></div>`).join("");
  }

  // Skip Rate by FT_ Test
  const skData = ftCols.map(c => ({ name: c.replace("FT_", ""), skips: filtered.filter(r => r[c] === "SKIP").length })).filter(r => r.skips > 0).sort((a, b) => b.skips - a.skips).slice(0, 15);
  function htmlSkip() {
    if (!skData.length) return '<p style="color:#888">No data</p>';
    const mx = skData[0].skips;
    return skData.map(({ name, skips }) => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px"><div style="min-width:130px;font-size:10px;color:#334155;text-align:right;white-space:nowrap">${name}</div><div style="flex:1;background:#f1f5f9;height:16px;border-radius:3px"><div style="width:${(skips/mx*100).toFixed(1)}%;height:100%;background:#94a3b8;border-radius:3px"></div></div><div style="min-width:28px;font-size:10px;font-weight:700;color:#64748b;text-align:right">${skips}</div></div>`).join("");
  }

  const CHARTS_HTML = {
    trend: `<div class="section" style="break-inside:avoid"><h2>📈 Failure Rate Trend</h2>${svgTrend()}</div>`,
    station: `<div class="section" style="break-inside:avoid"><h2>🏭 Failures by Station</h2>${htmlStation()}</div>`,
    passfail: `<div class="section" style="break-inside:avoid"><h2>✅ Pass / Fail by Station</h2>${htmlPassFail()}</div>`,
    pie: `<div class="section" style="break-inside:avoid"><h2>📊 PASS / FAIL Distribution</h2>${svgPie()}</div>`,
    heatmap: `<div class="section" style="break-inside:avoid"><h2>🗺 Heatmap Tests x Stations (% FAIL)</h2>${htmlHeatmap()}</div>`,
    ftbars: `<div class="section" style="break-inside:avoid"><h2>🔬 Failures by FT_ Test</h2>${htmlFT()}</div>`,
    hourly: `<div class="section" style="break-inside:avoid"><h2>🕐 Volume & Failures by Hour</h2>${htmlHourly()}</div>`,
    duration: `<div class="section" style="break-inside:avoid"><h2>⏱ Average Test Duration by Station</h2>${htmlDuration()}</div>`,
    idle: `<div class="section" style="break-inside:avoid"><h2>💤 Average Idle Time by Station</h2>${htmlIdle()}</div>`,
    skip: `<div class="section" style="break-inside:avoid"><h2>⏭ Skip Count by FT_ Test</h2>${htmlSkip()}</div>`,
  };

  const html = `<!DOCTYPE html><html lang="en" translate="no"><head><meta charset="UTF-8"/><meta name="google" content="notranslate"/><title>RADI Report - ${opts.siteName || "Site"} — ${period}</title>
  <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;background:#fff;padding:28px;font-size:12px}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;border-bottom:3px solid #2563eb;padding-bottom:14px}.logo{font-size:20px;font-weight:900;color:#2563eb}.logo span{color:#0f172a}.header-meta{text-align:right;color:#64748b;font-size:11px;line-height:1.9}.kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:20px}.kpi{border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px}.kpi-lbl{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:3px}.kpi-val{font-size:20px;font-weight:900;color:#2563eb}.red{color:#ef4444}.ora{color:#f59e0b}.section{margin-bottom:20px;border:1px solid #e2e8f0;border-radius:8px;padding:14px}h2{font-size:12px;font-weight:700;margin-bottom:10px;color:#0f172a;border-bottom:1px solid #f1f5f9;padding-bottom:6px}table.s{width:100%;border-collapse:collapse;font-size:11px}table.s th{background:#f8fafc;padding:6px 10px;text-align:left;color:#64748b;font-weight:700;border-bottom:2px solid #e2e8f0}table.s td{padding:5px 10px;border-bottom:1px solid #f1f5f9}.bf{background:#fef2f2;color:#ef4444;border-radius:4px;padding:1px 6px;font-weight:700}.bp{background:#f0fdf4;color:#10b981;border-radius:4px;padding:1px 6px;font-weight:700}.conclusion{border:2px solid #e2e8f0;border-radius:8px;padding:14px;min-height:70px;white-space:pre-wrap;font-size:12px;line-height:1.6}@media print{.section{break-inside:avoid}.kpis{break-inside:avoid}}</style>
  </head><body>
  <div class="header"><div><img src="${FUTUREDIAL_LOGO}" alt="FutureDial" style="height:38px;display:block;margin-bottom:5px"/><div style="font-size:10px;color:#64748b">Quality Report - RADI Platform</div>${opts.siteName ? `<div style="font-size:13px;font-weight:700;margin-top:5px">&#128205; ${opts.siteName}</div>` : ""}</div><div class="header-meta"><div><strong>Period:</strong> ${period}</div><div><strong>Transactions:</strong> ${total.toLocaleString()}</div><div><strong>Generated on:</strong> ${new Date().toLocaleString("en-US")}</div></div></div>
  <div class="kpis"><div class="kpi"><div class="kpi-lbl">Total</div><div class="kpi-val">${total.toLocaleString()}</div></div><div class="kpi"><div class="kpi-lbl">Failures</div><div class="kpi-val red">${failures.toLocaleString()}</div></div><div class="kpi"><div class="kpi-lbl">failure rate</div><div class="kpi-val ora">${failPct}%</div></div><div class="kpi"><div class="kpi-lbl">Models</div><div class="kpi-val">${models}</div></div><div class="kpi"><div class="kpi-lbl">Stations</div><div class="kpi-val">${stations}</div></div></div>
  ${top5.length ? `<div class="section"><h2>🔴 Top 5 - Failure Causes</h2><table class="s"><thead><tr><th>Cause</th><th style="text-align:right">Occurrences</th></tr></thead><tbody>${top5.map(([r, c]) => `<tr><td>${r}</td><td style="text-align:right;font-weight:700;color:#ef4444">${c}</td></tr>`).join("")}</tbody></table></div>` : ""}
  <div class="section"><h2>📱 Failures by Model</h2><table class="s"><thead><tr><th>Model</th><th style="text-align:right">Failures</th><th style="text-align:right">Total</th><th style="text-align:right">%</th></tr></thead><tbody>${modelRows.map(r => `<tr><td>${r.model}</td><td style="text-align:right"><span class="bf">${r.fail}</span></td><td style="text-align:right">${r.total}</td><td style="text-align:right;font-weight:700;color:${+r.pct >= 50 ? "#ef4444" : +r.pct >= 20 ? "#f59e0b" : "#10b981"}">${r.pct}%</td></tr>`).join("")}</tbody></table></div>
  ${selectedCharts.includes("trend") ? CHARTS_HTML.trend : ""}
  ${selectedCharts.includes("passfail") ? CHARTS_HTML.passfail : ""}
  ${selectedCharts.includes("station") ? CHARTS_HTML.station : ""}
  ${selectedCharts.includes("pie") ? CHARTS_HTML.pie : ""}
  ${selectedCharts.includes("heatmap") ? CHARTS_HTML.heatmap : ""}
  ${selectedCharts.includes("ftbars") ? CHARTS_HTML.ftbars : ""}
  ${selectedCharts.includes("hourly") ? CHARTS_HTML.hourly : ""}
  ${selectedCharts.includes("duration") ? CHARTS_HTML.duration : ""}
  ${selectedCharts.includes("idle") ? CHARTS_HTML.idle : ""}
  ${selectedCharts.includes("skip") ? CHARTS_HTML.skip : ""}
  <div class="section"><h2>📝 Conclusion</h2><div class="conclusion">${(opts.conclusion || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</div></div>
  <div style="margin-top:16px;text-align:center;color:#94a3b8;font-size:9px;border-top:1px solid #e2e8f0;padding-top:10px">Report generated by RADI Dashboard • ${new Date().toLocaleDateString("en-US")}</div>
  <script>window.onload=()=>setTimeout(()=>window.print(),500);<\/script></body></html>`;

  const w = window.open("", "_blank");
  if (!w) { alert("Popup blocked! Please allow popups for this site."); return; }
  w.document.write(html);
  w.document.close();
}

function ReportModal({ filtered, onClose }) {
  const [selCharts, setSelCharts] = useState(["trend", "passfail", "station", "pie", "heatmap", "ftbars"]);
  const [siteName, setSiteName] = useState("");
  const [conclusion, setConclusion] = useState("");
  const CHARTS = [
    { id: "trend", icon: "📈", label: "Failure Rate Trend" },
    { id: "passfail", icon: "✅", label: "Pass / Fail by Station" },
    { id: "station", icon: "🏭", label: "Failures by Station" },
    { id: "pie", icon: "📊", label: "Distribution PASS/FAIL" },
    { id: "heatmap", icon: "🗺", label: "Heatmap Tests x Stations" },
    { id: "ftbars", icon: "🔬", label: "Failures by FT_ Test" },
    { id: "hourly", icon: "🕐", label: "Volume & Failures by Hour" },
    { id: "duration", icon: "⏱", label: "Test Duration by Station" },
    { id: "idle", icon: "💤", label: "Idle Time by Station" },
    { id: "skip", icon: "⏭", label: "Skip Count by FT_ Test" },
  ];
  const toggle = id => setSelCharts(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 600, maxWidth: "95vw", boxShadow: "0 24px 60px rgba(0,0,0,.25)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: T.text, display: "flex", alignItems: "center", gap: 8 }}><AppIcon icon="📄" size={16} /> Generate PDF Report</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.textLight, cursor: "pointer", lineHeight: 1, display: "flex" }}><AppIcon icon="✕" size={16} /></button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.textLight, display: "block", marginBottom: 5, letterSpacing: 1, textTransform: "uppercase" }}>Site / Client Name</label>
            <input value={siteName} onChange={e => setSiteName(e.target.value)} placeholder="e.g. Retourpolis Paris"
              style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", color: T.text, fontFamily: "inherit" }} />
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: T.textLight, letterSpacing: 1, textTransform: "uppercase" }}>Charts to include</label>
              <button onClick={() => setSelCharts(selCharts.length === CHARTS.length ? [] : CHARTS.map(c => c.id))} style={{ background: "transparent", border: "none", color: T.accent, fontSize: 11, cursor: "pointer", fontWeight: 700 }}>
                {selCharts.length === CHARTS.length ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {CHARTS.map(c => (
                <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px 12px", borderRadius: 8,
                  background: selCharts.includes(c.id) ? T.accentBg : "#f8fafc", border: `1px solid ${selCharts.includes(c.id) ? T.accent : T.border}` }}>
                  <input type="checkbox" checked={selCharts.includes(c.id)} onChange={() => toggle(c.id)} style={{ accentColor: T.accent, width: 15, height: 15 }} />
                  <span style={{ fontSize: 12, color: selCharts.includes(c.id) ? T.accent : T.text, fontWeight: selCharts.includes(c.id) ? 600 : 400, display: "flex", alignItems: "center", gap: 7 }}>
                    <AppIcon icon={c.icon} size={12} />{c.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.textLight, display: "block", marginBottom: 5, letterSpacing: 1, textTransform: "uppercase" }}>Conclusion (free text)</label>
            <textarea value={conclusion} onChange={e => setConclusion(e.target.value)} rows={4}
              placeholder="Enter observations and conclusions before generating..."
              style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, outline: "none", resize: "vertical", color: T.text, fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ border: `1px solid ${T.border}`, background: "transparent", color: T.textMid, borderRadius: 8, padding: "9px 20px", cursor: "pointer", fontSize: 13 }}>Cancel</button>
            <button onClick={() => { generatePDFReport(filtered, selCharts, { siteName, conclusion }); }} className="hover-lift" style={{
              background: "linear-gradient(135deg, #2563eb, #4f46e5)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 24px",
              cursor: "pointer", fontSize: 13, fontWeight: 700, boxShadow: "0 4px 12px rgba(37,99,235,.35)",
              display: "flex", alignItems: "center", gap: 7
            }}><AppIcon icon="📄" size={13} /> Generate Report</button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════
// IDLE TIME TAB
// ══════════════════════════════════════════════════
function IdleTimeTab({ data }) {
  const toSec = t => {
    if (!t) return null;
    const p = String(t).split(":");
    return p.length === 3 ? +p[0] * 3600 + +p[1] * 60 + +p[2] : null;
  };

  const { gaps, byStation, byHour } = useMemo(() => {
    // Parse a row's own test duration, in seconds. Prefer "Total test time"
    // (ms — the field the Test Duration tab uses, backed by the API's "exeT"),
    // fall back to "Test Duration"/"Duration" (already seconds — CSV-only
    // columns some exports use instead).
    const getDurSec = r => {
      const msRaw = r["Total test time"] || r["total test time"] || r["Total Test Time"] || "";
      const ms = parseFloat(String(msRaw).replace(/,/g, ""));
      if (!isNaN(ms) && ms > 0) return ms / 1000;
      const secRaw = r["Test Duration"] || r["Duration"] || "";
      const sec = Number(secRaw);
      return !isNaN(sec) && sec > 0 ? sec : 0;
    };

    // Group by station+date, sort by time, compute gaps
    const groups = {};
    data.forEach(r => {
      const st = r["Station ID"] || "Unknown";
      const dt = r._dateStr || "";
      const sec = toSec(r["Hour"] || r["Time"] || r["Start Time"] || "");
      if (!dt || sec === null) return;
      const key = `${st}||${dt}`;
      if (!groups[key]) groups[key] = { st, dt, op: r["Operator"] || "—", rows: [] };
      groups[key].rows.push({ sec, dur: getDurSec(r) });
    });

    const allGaps = [];
    const stMap = {};
    const hourMap = {};

    Object.values(groups).forEach(({ st, dt, op, rows }) => {
      rows.sort((a, b) => a.sec - b.sec);
      for (let i = 1; i < rows.length; i++) {
        const raw = rows[i].sec - rows[i - 1].sec;
        if (raw <= 0) continue; // ignore negative/zero — out-of-order or duplicate timestamps
        // Idle time is the gap MINUS the previous transaction's own test
        // duration — that time was spent actively testing, not sitting idle.
        // Clamp at 0 rather than dropping the pair: back-to-back tests with
        // no dead time at all are a real (and good!) data point, not noise.
        const g = Math.max(raw - rows[i - 1].dur, 0);
        if (g > 900) continue; // ignore idle > 15min — treated as a break/shift change
        const gMin = g / 60;
        allGaps.push(gMin);
        if (!stMap[st]) stMap[st] = { st, op, gaps: [] };
        stMap[st].gaps.push(gMin);
        // hour bucket from rows[i] (hour of day 0-23)
        const hr = Math.floor(rows[i].sec / 3600);
        if (!hourMap[hr]) hourMap[hr] = [];
        hourMap[hr].push(gMin);
      }
    });

    const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    const med = arr => { if (!arr.length) return 0; const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

    const byStation = Object.values(stMap).map(({ st, op, gaps }) => ({
      st, op,
      n: gaps.length,
      avg: avg(gaps),
      med: med(gaps),
      min: Math.min(...gaps),
      max: Math.max(...gaps),
      over5: gaps.filter(g => g > 5).length,
      pctOver5: gaps.length ? (gaps.filter(g => g > 5).length / gaps.length * 100) : 0,
    })).sort((a, b) => a.avg - b.avg);

    const byHour = Array.from({ length: 24 }, (_, h) => ({
      h, label: `${String(h).padStart(2, "0")}h`,
      avg: hourMap[h]?.length ? avg(hourMap[h]) : null,
    })).filter(x => x.avg !== null);

    return { gaps: allGaps, byStation, byHour };
  }, [data]);

  if (!gaps.length) return (
    <div style={{ textAlign: "center", padding: 80, color: T.textLight }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⏱</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>No gap data available</div>
      <div style={{ fontSize: 12, marginTop: 6 }}>Make sure the Hour column (HH:MM:SS) is present</div>
    </div>
  );

  const avgAll = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const fastest = byStation[0];
  const slowest = byStation[byStation.length - 1];
  const over5Total = gaps.filter(g => g > 5).length;

  const fmtMin = v => fmtDuration(v);
  const gapColor = v => v < 2 ? T.pass : v < 5 ? "#f59e0b" : T.fail;
  const gapBg = v => v < 2 ? T.passBg : v < 5 ? "#fffbeb" : T.failBg;

  // Histogram buckets
  const BUCKETS = [
    { label: "0–1 min", min: 0, max: 1 },
    { label: "1–2 min", min: 1, max: 2 },
    { label: "2–5 min", min: 2, max: 5 },
    { label: "5–10 min", min: 5, max: 10 },
    { label: ">10 min", min: 10, max: Infinity },
  ];
  const hist = BUCKETS.map(b => ({ ...b, count: gaps.filter(g => g >= b.min && g < b.max).length }));
  const histMax = Math.max(...hist.map(b => b.count), 1);
  const histColors = [T.pass, "#34d399", "#f59e0b", "#f97316", T.fail];

  // Bar chart max
  const barMax = byStation.length ? byStation[byStation.length - 1].avg : 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: -8 }}>
        <SectionTitle>⏱ Idle Time — Dead time between transactions</SectionTitle>
        <InfoTip width={280}>
          Idle time is the gap between two consecutive transactions on the <strong>same station, same day</strong>,
          minus the first transaction's own test duration — that time was spent actively testing, not sitting idle.
          <br /><br />
          Gaps longer than <strong>15 minutes are excluded entirely</strong> from every number and chart on this page —
          they're treated as breaks, shift changes, or the station being offline, not real dead time between tests.
        </InfoTip>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
        <KpiCard label="Average Gap global" value={fmtMin(avgAll)} sub={`${gaps.length} gaps calculated`} color={T.accent} />
        <KpiCard label="Fastest Station" value={fastest?.st || "—"} sub={fastest ? fmtMin(fastest.avg) : ""} color={T.pass} />
        <KpiCard label="Slowest Station" value={slowest?.st || "—"} sub={slowest ? fmtMin(slowest.avg) : ""} color={T.fail} />
        <div style={{ position: "relative" }}>
          <KpiCard label="Gaps > 5 min" value={over5Total.toLocaleString()} sub={`${(over5Total / gaps.length * 100).toFixed(1)}% excessive idle`} color="#f59e0b" />
          <div style={{ position: "absolute", top: 14, right: 16 }}>
            <InfoTip width={260} align="right">
              Counts gaps between <strong>5 and 15 minutes</strong> — a "slower than ideal" warning zone.
              <br /><br />
              Gaps over 15 minutes aren't in this count at all — they're excluded from the page entirely (see the ⓘ next to the page title).
            </InfoTip>
          </div>
        </div>
      </div>

      {/* Bar chart: avg gap per station */}
      <Card title="📊 Average gap by station (fastest to slowest)">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {byStation.map(s => (
            <div key={s.st} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ minWidth: 110, fontSize: 11, color: T.textMid, textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.st}</div>
              <div style={{ flex: 1, background: T.border, borderRadius: 4, height: 18, overflow: "hidden" }}>
                <div style={{ width: `${(s.avg / barMax) * 100}%`, height: "100%", background: gapColor(s.avg), borderRadius: 4, transition: "width .4s", minWidth: 4 }} />
              </div>
              <div style={{ minWidth: 52, fontSize: 11, fontWeight: 700, color: gapColor(s.avg), textAlign: "right" }}>{fmtMin(s.avg)}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 10, color: T.textLight, alignItems: "center" }}>
          <span style={{ color: T.pass }}>● &lt; 2 min (fast)</span>
          <span style={{ color: "#f59e0b" }}>● 2-5 min (medium)</span>
          <span style={{ color: T.fail }}>● &gt; 5 min (slow)</span>
          <InfoTip width={250}>Gaps over 15 minutes are excluded from these averages entirely — see the ⓘ next to the page title for why.</InfoTip>
        </div>
      </Card>

      {/* Hourly trend */}
      {byHour.length >= 2 && (
        <Card title="🕐 Average gap by hour of day">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={byHour} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="label" tick={{ fill: T.textLight, fontSize: 10 }} />
              <YAxis tick={{ fill: T.textLight, fontSize: 10 }} tickFormatter={v => `${v.toFixed(1)}m`} />
              <Tooltip formatter={v => [fmtDuration(v), "Average Gap"]} contentStyle={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 11 }} />
              <Line type="monotone" dataKey="avg" stroke={T.accent} strokeWidth={2} dot={{ r: 3, fill: T.accent }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Distribution histogram */}
      <Card title="📈 Gap Distribution">
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", height: 120 }}>
          {hist.map((b, i) => (
            <div key={b.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: histColors[i] }}>{b.count}</div>
              <div style={{ width: "100%", background: histColors[i], borderRadius: "4px 4px 0 0", height: `${(b.count / histMax) * 90}px`, minHeight: b.count ? 4 : 0, transition: "height .4s" }} />
              <div style={{ fontSize: 9, color: T.textLight, textAlign: "center", whiteSpace: "nowrap" }}>{b.label}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Detailed table */}
      <Card title="🗂 Station Detail">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                {[
                  { h: "Station ID", tip: null },
                  { h: "Operator", tip: "The operator assigned to this station, as recorded in the export." },
                  { h: "Gap Count", tip: "How many gaps between consecutive transactions were counted for this station. Gaps over 15 minutes (breaks, shift changes, downtime) are excluded — see the ⓘ next to the page title." },
                  { h: "Average Gap", tip: "The mean idle time between two consecutive transactions on this station. Colored green under 2 min, orange 2–5 min, red over 5 min." },
                  { h: "Median Gap", tip: "The middle value of all gaps on this station — less skewed by the occasional very long or very short gap than the average is." },
                  { h: "Min Gap", tip: "The shortest gap recorded between two consecutive transactions on this station." },
                  { h: "Max Gap", tip: "The longest gap recorded on this station. Capped at 15 minutes, since anything longer is excluded from the page entirely." },
                  { h: "% > 5min", tip: "Share of this station's gaps that were slower than 5 minutes — a rough measure of how often handling was slow or manual. Red above 20%, orange 5–20%." },
                ].map(({ h, tip }) => (
                  // Native `title` (not the custom InfoTip bubble) on purpose here: this table sits inside
                  // an overflow-x:auto wrapper for horizontal scrolling, which clips any absolutely-positioned
                  // custom tooltip that tries to open near the table's edge. The browser's native tooltip
                  // renders outside the DOM/CSS layer entirely, so it's never clipped.
                  <th key={h} title={tip || undefined} style={{ padding: "8px 10px", textAlign: h === "Station ID" || h === "Operator" ? "left" : "right", color: T.textLight, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap", borderBottom: `1px solid ${T.border}`, cursor: tip ? "help" : "default" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: h === "Station ID" || h === "Operator" ? "flex-start" : "flex-end" }}>
                      {h}
                      {tip && <Icon size={11}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="11.5" /><line x1="12" y1="8" x2="12.01" y2="8" /></Icon>}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byStation.map((s, i) => (
                <tr key={s.st} style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? "transparent" : T.accentBg + "33" }}>
                  <td style={{ padding: "7px 10px", color: T.accent, fontWeight: 700, fontFamily: "monospace", fontSize: 11 }} title="Station ID">{s.st}</td>
                  <td style={{ padding: "7px 10px", color: T.textMid, fontSize: 11 }} title="Operator assigned to this station">{s.op}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", color: T.text }} title="Number of gaps computed (pairs of consecutive transactions, same station same day, gap ≤ 15 min)">{s.n.toLocaleString()}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }} title={`Mean idle time: ${fmtMin(s.avg)} — Color: green < 2min, orange 2-5min, red > 5min`}>
                    <span style={{ background: gapBg(s.avg), color: gapColor(s.avg), borderRadius: 5, padding: "2px 8px", fontWeight: 700, fontSize: 11 }}>{fmtMin(s.avg)}</span>
                  </td>
                  <td style={{ padding: "7px 10px", textAlign: "right", color: T.textMid }} title={`Median gap: ${fmtMin(s.med)} — Middle value, less affected by outliers`}>{fmtMin(s.med)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", color: T.pass, fontWeight: 600 }} title={`Shortest gap recorded: ${fmtMin(s.min)}`}>{fmtMin(s.min)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", color: T.fail, fontWeight: 600 }} title={`Longest gap recorded: ${fmtMin(s.max)} (gaps > 15min are excluded)`}>{fmtMin(s.max)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", color: s.pctOver5 > 20 ? T.fail : s.pctOver5 > 5 ? "#f59e0b" : T.textMid, fontWeight: s.pctOver5 > 5 ? 700 : 400 }} title={`${s.over5} out of ${s.n} gaps exceeded 5 minutes — ${s.pctOver5.toFixed(1)}% — Threshold: red > 20%, orange 5-20%`}>{s.pctOver5.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════
// ERROR BOUNDARY
// ══════════════════════════════════════════════════
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, info) { console.error("Dashboard render error:", e, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: "center", color: T.fail, background: T.failBg, borderRadius: 12, margin: 20, border: `1px solid ${T.fail}33` }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><AppIcon icon="⚠️" size={30} strokeWidth={1.5} /></div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Rendering error on this tab</div>
          <div style={{ fontSize: 12, color: T.textMid, marginBottom: 16 }}>{String(this.state.error?.message || this.state.error)}</div>
          <button onClick={() => this.setState({ error: null })} style={{
            background: T.accent, color: "#fff", border: "none", borderRadius: 8,
            padding: "8px 20px", cursor: "pointer", fontSize: 13, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 6, margin: "0 auto"
          }}><AppIcon icon="↩" size={12} /> Back</button>
        </div>
      );
    }
    return this.props.children;
  }
}


const NAV = [
  { id: "overview", icon: "📊", label: "Overview" },
  { id: "global", icon: "🌐", label: "Global Analysis" },
  { id: "health", icon: "🏥", label: "Station Health" },
  { id: "tests", icon: "🔬", label: "Test Analysis" },
  { id: "cross", icon: "🔀", label: "Cross-Station" },
  { id: "repeat", icon: "🔁", label: "Repeat Devices" },
  { id: "time", icon: "⏰", label: "Time Patterns" },
  { id: "skip", icon: "⏭", label: "Skip Rate" },
  { id: "duration", icon: "⏱", label: "Test Duration" },
  { id: "idle", icon: "💤", label: "Idle Time" },
  { id: "raw", icon: "📄", label: "Raw Data" },
];

// Overall dashboard scale — change this ONE number to make everything bigger
// or smaller. Anything sized in viewport units (100vh/100vw) has to be
// divided by this same factor wherever it's used below, because CSS `zoom`
// re-scales an already-resolved vh/vw length instead of the viewport itself
// — without that compensation, "100vh" would render as 100vh * ZOOM tall and
// spill off the bottom of the screen (which is what was happening).
const ZOOM = 1.0;

// ══════════════════════════════════════════════════
// MINI CALENDAR — a small self-contained month-grid date picker used by the
// RADI API sync menu (dark-themed to match that menu, no external library).
// ══════════════════════════════════════════════════
function MiniCalendar({ selected, onSelect, accent = "#10b981" }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = selected || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const year = viewMonth.getFullYear(), month = viewMonth.getMonth();
  const firstDow = new Date(year, month, 1).getDay(); // 0 = Sunday
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const MN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const DOW = ["S", "M", "T", "W", "T", "F", "S"];
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, padding: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <button type="button" onClick={() => setViewMonth(new Date(year, month - 1, 1))} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14, padding: "2px 8px", lineHeight: 1 }}>‹</button>
        <span style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 700 }}>{MN[month]} {year}</span>
        <button type="button" onClick={() => setViewMonth(new Date(year, month + 1, 1))} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14, padding: "2px 8px", lineHeight: 1 }}>›</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, marginBottom: 3 }}>
        {DOW.map((d, i) => <div key={i} style={{ textAlign: "center", fontSize: 9, color: "#64748b", fontWeight: 700 }}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const cellDate = new Date(year, month, d);
          const isSel = isSameDay(cellDate, selected);
          const isToday = isSameDay(cellDate, today);
          return (
            <button type="button" key={i} onClick={() => onSelect(cellDate)} style={{
              width: "100%", aspectRatio: "1", borderRadius: 5, fontSize: 11, cursor: "pointer",
              background: isSel ? accent : "transparent",
              color: isSel ? "#052e1f" : isToday ? accent : "#cbd5e1",
              border: isToday && !isSel ? `1px solid ${accent}` : "1px solid transparent",
              fontWeight: isSel || isToday ? 700 : 400
            }}>{d}</button>
          );
        })}
      </div>
    </div>
  );
}

function Dashboard() {
  const [rawData, setRawData] = useState([]);
  const [failOnlyTests, setFailOnlyTests] = useState([]); // tests in result_FAIL with no FT_ column
  const [filters, setFilters] = useState({});
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rawSearch, setRawSearch] = useState("");
  const [showReportModal, setShowReportModal] = useState(false);
  const fileRef = useRef();

  // ── Signed-in user (Microsoft Entra ID) ──
  // By the time Dashboard renders, App (below) has already guaranteed we're
  // authenticated — this just gives us the account + a way to attach proof
  // of that sign-in to every request our own backend receives.
  const { instance, accounts } = useMsal();
  // Prefer the account explicitly set as active by index.js on page refresh
  // over the raw getAllAccounts()[0] — ensures the right user is used when
  // multiple Microsoft accounts are cached in the browser.
  const account = instance.getActiveAccount() || accounts[0];

  // Every API call to our backend carries the signed-in user's Microsoft ID
  // token as a Bearer header, so the backend can verify (signature + issuer
  // + audience + expiry) that the request really came from someone signed
  // into this organization's Microsoft 365 before it talks to FutureDial —
  // today literally anyone who found the backend's URL could call it.
  const authFetch = async (url, options = {}) => {
    if (!account) throw new Error("Not signed in — please refresh the page.");
    let tokenResponse;
    try {
      tokenResponse = await instance.acquireTokenSilent({ ...loginRequest, account });
    } catch (err) {
      // IMPORTANT: do NOT auto-redirect to Microsoft from here. This function
      // runs inside a useEffect on every mount (i.e. on every page load), and
      // some browsers/network setups (third-party-cookie blocking is common)
      // make the silent MSAL renewal fail every single time — if we reacted
      // to that by calling acquireTokenRedirect(), the app would immediately
      // bounce to login.microsoftonline.com and back on EVERY load, which
      // looks exactly like "the page goes blank and won't load, even in a
      // new tab" (this was a real bug, now fixed). Surface a plain error
      // instead and let the "Sign out" button be the deliberate way back in.
      if (err instanceof InteractionRequiredAuthError) {
        throw new Error("Your Microsoft sign-in has expired. Use \"Sign out\" in the sidebar, then sign in again.");
      }
      throw err;
    }
    return fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${tokenResponse.idToken}` },
    });
  };

  const handleFile = e => {
    const file = e.target.files[0]; if (!file) return;
    setLoading(true); setError("");
    setRawData([]); setFilters({}); setRawSearch(""); setFailOnlyTests([]);
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const { normalized, failOnlyTests } = normalizeRows(json);
        setFailOnlyTests(failOnlyTests);
        setRawData(normalized); setFilters({}); setLoading(false);
      } catch (err) { setError("Failed to parse: " + err.message); setLoading(false); }
    };
    reader.readAsBinaryString(file);
  };

  // ── Live sync from the RADI/FutureDial API (via our local backend) ──
  // FutureDial gives us one dedicated login PER CLIENT COMPANY now (not one
  // admin account that sees everything), so the backend exposes a list of
  // configured clients and we let the user pick one, several, or all of them,
  // plus an explicit start/end date-time for the export.
  const [apiLoading, setApiLoading] = useState(false);
  const [apiSyncMessage, setApiSyncMessage] = useState(""); // live progress text shown on the sync button while a job runs
  const [showApiMenu, setShowApiMenu] = useState(false);
  const [clients, setClients] = useState([]); // [{key, label}]
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientsError, setClientsError] = useState("");
  // Distinguishes "your Microsoft sign-in expired" from an actual backend-
  // unreachable error, so we can show a "Sign in again" button instead of the
  // misleading "is it running?" message — see the useEffect below.
  const [clientsAuthExpired, setClientsAuthExpired] = useState(false);

  // Internal format for apiStart/apiEnd is "YYYY-MM-DDTHH:mm" (local time) —
  // same shape a native datetime-local input would give us, just built by
  // our own calendar + hour/minute pickers instead.
  const toLocalInputValue = d => {
    const p = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const splitDateTime = val => { const [d, t] = String(val || "").split("T"); return { d: d || "", t: t || "00:00" }; };
  const joinDateTime = (d, t) => `${d}T${t || "00:00"}`;

  const [apiStart, setApiStart] = useState(() => toLocalInputValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)));
  const [apiEnd, setApiEnd] = useState(() => toLocalInputValue(new Date()));
  const [activeDateField, setActiveDateField] = useState("start"); // which field the calendar below is editing

  // Quick-range shortcuts (Today / Last 7 days / Last 29 days) — set both
  // From and To in one click instead of clicking through the calendar twice.
  const applyQuickRange = kind => {
    const now = new Date();
    let start;
    if (kind === "today") start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    else if (kind === "7d") start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    else start = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000); // "29d"
    setApiStart(toLocalInputValue(start));
    setApiEnd(toLocalInputValue(now));
  };
  // 1 = transaction creation time ("timeCreated"), 2 = server record time ("serverTime") — matches the API doc.
  const [apiTimeType, setApiTimeType] = useState("2");

  // Load the list of configured client companies whenever the active account
  // is available (including after a page refresh where MSAL restores the
  // session from localStorage). Using account?.homeAccountId as the dep
  // means this re-runs if the user signs out and back in with a different
  // account, and — crucially — still runs exactly once on a normal page load.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch captures account from closure; dep on homeAccountId is the stable identity
  useEffect(() => {
    if (!account) return;
    authFetch(`${API_BASE}/api/radi-clients`)
      .then(res => { if (!res.ok) throw new Error(`Server responded ${res.status}`); return res.json(); })
      .then(list => {
        if (!Array.isArray(list)) throw new Error("Unexpected response shape");
        setClients(list);
        setSelectedClients(list.map(c => c.key)); // default: all clients selected
      })
      .catch(err => {
        // authFetch throws auth-related messages when the Microsoft sign-in
        // session is stale — very common after leaving the tab idle overnight.
        // Show the raw message + a "Sign in again" button instead of the
        // misleading "is it running?" backend error in that case.
        const isAuthError = err?.message?.startsWith("Not signed in") ||
          err?.message?.startsWith("Your Microsoft") ||
          (err instanceof Error && err.message.includes("sign-in has expired"));
        if (isAuthError) {
          setClientsAuthExpired(true);
          setClientsError(err.message);
        } else {
          setClientsAuthExpired(false);
          setClientsError("Couldn't reach the backend to list client companies — is it running?");
        }
      });
  }, [account?.homeAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sleep = ms => new Promise(r => setTimeout(r, ms));

  const handleApiSync = async () => {
    setShowApiMenu(false);
    if (selectedClients.length === 0) { setError("Select at least one client company first."); return; }
    setApiLoading(true); setError(""); setApiSyncMessage("Starting…");
    setRawData([]); setFilters({}); setRawSearch(""); setFailOnlyTests([]);
    try {
      const params = new URLSearchParams();
      params.set("clients", selectedClients.join(","));
      params.set("start", new Date(apiStart).toISOString());
      params.set("end", new Date(apiEnd).toISOString());
      params.set("timetype", apiTimeType);

      // Step 1: kick off the job — this responds instantly, so it can never
      // time out no matter how long FutureDial itself takes.
      const startRes = await authFetch(`${API_BASE}/api/radi-transactions/start?${params.toString()}`);
      if (!startRes.ok) {
        const body = await startRes.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${startRes.status}`);
      }
      const { jobId } = await startRes.json();
      if (!jobId) throw new Error("Backend didn't return a job id — is it running the latest version?");

      // Step 2: poll for the result. Each poll is a tiny, fast request, so
      // this can safely run for minutes without ever hitting a proxy timeout.
      const startedAt = Date.now();
      // A full year gets split into ~12 chunks per client and each chunk can
      // take up to ~2 minutes with FutureDial, so a big multi-client/full-year
      // sync can legitimately run for a while. Give it a generous ceiling
      // rather than a fixed 4 minutes — the progress text below tells you
      // it's still working.
      const MAX_WAIT_MS = 30 * 60 * 1000; // give up after 30 minutes
      let job = null;
      while (true) {
        if (Date.now() - startedAt > MAX_WAIT_MS) throw new Error("Timed out waiting for the sync to finish (30+ minutes) — try fewer clients or a shorter range.");
        const statusRes = await authFetch(`${API_BASE}/api/radi-transactions/status?jobId=${encodeURIComponent(jobId)}`);
        if (!statusRes.ok) {
          const body = await statusRes.json().catch(() => ({}));
          throw new Error(body.error || `Server responded ${statusRes.status}`);
        }
        job = await statusRes.json();
        if (job.status !== "running") break;
        const elapsed = `${Math.round((Date.now() - startedAt) / 1000)}s`;
        const prog = job.progress && job.progress.total ? ` — batch ${job.progress.completed}/${job.progress.total}` : "";
        setApiSyncMessage(`Fetching… (${elapsed}${prog})`);
        await sleep(1500);
      }

      if (job.status === "error") throw new Error(job.message || "Sync failed.");

      // The actual rows are NOT included in /status responses — the backend
      // keeps those small and fast on purpose (a job pulling in hundreds of
      // thousands of rows would otherwise blow up memory just answering a
      // status poll). Now that the job is done, fetch the data separately;
      // the backend streams it straight off disk rather than holding it all
      // in memory, which is also what fixed the "Failed to fetch"/heap
      // crashes on big multi-client or year-long syncs.
      setApiSyncMessage("Downloading results…");
      // The backend now serves the result in PAGES instead of one giant
      // response — a single multi-hundred-MB JSON array for a large
      // multi-client/multi-week sync could take long enough (or be large
      // enough) to get cut off by Render's proxy before it finished, which
      // is what used to surface as "Unexpected end of JSON input" — and
      // retrying the exact same oversized request just hit the same wall
      // again. Fetching bounded pages instead means each request is small
      // and fast enough to always complete, and a dropped page only costs
      // re-fetching that one page (still retried a few times below), not
      // the whole sync. The finished job's file sits on the server for 10
      // minutes, so re-reading any page of it is always safe.
      const RESULT_FETCH_RETRIES = 3;
      const RESULT_PAGE_LIMIT = 20000;
      let json = [];
      let offset = 0;
      let hasMore = true;
      while (hasMore) {
        let page = null;
        let lastFetchErr = null;
        for (let attempt = 1; attempt <= RESULT_FETCH_RETRIES; attempt++) {
          try {
            const resultRes = await authFetch(`${API_BASE}/api/radi-transactions/result?jobId=${encodeURIComponent(jobId)}&offset=${offset}&limit=${RESULT_PAGE_LIMIT}`);
            if (!resultRes.ok) {
              const body = await resultRes.json().catch(() => ({}));
              throw new Error(body.error || `Server responded ${resultRes.status}`);
            }
            page = await resultRes.json();
            lastFetchErr = null;
            break;
          } catch (err) {
            lastFetchErr = err;
            if (attempt < RESULT_FETCH_RETRIES) {
              setApiSyncMessage(`Downloading results… (retry ${attempt}/${RESULT_FETCH_RETRIES - 1} after a dropped connection)`);
              await sleep(2000);
            }
          }
        }
        if (lastFetchErr) {
          throw new Error(
            (lastFetchErr.message || "Download failed") +
            " — the download kept getting interrupted after " + RESULT_FETCH_RETRIES + " tries. Try again, or sync fewer clients / a shorter range at once."
          );
        }
        if (!page || !Array.isArray(page.rows)) throw new Error("Unexpected response shape from backend (expected {rows: [...]})");
        json = json.concat(page.rows);
        hasMore = !!page.hasMore;
        offset += page.rows.length;
        if (page.rows.length === 0) break; // safety net: never loop forever on an unexpected empty page
        if (page.total) setApiSyncMessage(`Downloading results… (${json.length.toLocaleString()} / ${page.total.toLocaleString()})`);
      }
      const clientErrors = Array.isArray(job.errors) ? job.errors : [];

      const failSummary = clientErrors.length
        ? `${clientErrors.length} client(s) failed: ` + clientErrors.map(e => `${e.client} (${e.message})`).join("; ")
        : "";

      if (json.length === 0) {
        setError(failSummary || "No transactions found for that period.");
      } else {
        // eslint-disable-next-line no-console
        console.log("[RADI API] sample raw record (first of " + json.length + "):", json[0]);
        const { normalized, failOnlyTests } = normalizeRows(json);
        setFailOnlyTests(failOnlyTests);
        setRawData(normalized); setFilters({});
        if (failSummary) setError("Loaded " + json.length + " row(s), but " + failSummary);
      }
      setApiLoading(false); setApiSyncMessage("");
    } catch (err) {
      setError("API sync failed: " + err.message + " — is the backend running? (see server/README)");
      setApiLoading(false); setApiSyncMessage("");
    }
  };

  //const filtered=useMemo(()=>rawData.filter(row=>
  //Object.entries(filters).every(([key,vals])=>!vals?.length||vals.includes(row[key]))
  //),[rawData,filters]);

  const filtered = useMemo(() => applyFiltersExcept(rawData, filters, []), [rawData, filters]);

  const kpis = useMemo(() => {
    const total = filtered.length;
    const failures = filtered.filter(r => r["Overall status"] === "FAILURE").length;
    const models = new Set(filtered.map(r => r["Model"]).filter(Boolean)).size;
    const stations = new Set(filtered.map(r => r["Station ID"]).filter(Boolean)).size;
    // Same "Total test time" parsing as the Test Duration tab — average across
    // every row that has a valid value, in minutes.
    const durations = filtered.map(r => {
      const v = r["Total test time"] || r["total test time"] || r["Total Test Time"] || "";
      const n = parseFloat(String(v).replace(/,/g, ""));
      return isNaN(n) || n <= 0 ? null : n;
    }).filter(n => n !== null);
    const avgDurationMin = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length / 60000 : null;
    return { total, failures, models, stations, avgDurationMin, failPct: total ? ((failures / total) * 100).toFixed(1) + "%" : "—" };
  }, [filtered]);

  const result2 = useMemo(() => {
    const bm = {}; const total = filtered.length;
    filtered.forEach(row => {
      const m = row["Model"] || "Unknown";
      if (!bm[m]) bm[m] = { label: m, failCount: 0, total: 0 };
      bm[m].total++;
      const f = row["result_FAIL"] || row["Overall status"];
      if (f && f !== "SUCCESS" && f !== "PASS" && f !== "") bm[m].failCount++;
    });
    return Object.values(bm).map(r => ({ ...r, pctTotal: pct(r.failCount, total), pctModel: pct(r.failCount, r.total) }));
  }, [filtered]);

  const trendData = useMemo(() => {
    const bd = {};
    filtered.forEach(row => {
      const d = row._dateStr; if (!d) return;
      if (!bd[d]) bd[d] = { date: d, total: 0, fail: 0 };
      bd[d].total++; if (row["Overall status"] === "FAILURE") bd[d].fail++;
    });
    return Object.values(bd).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({ ...d, failPct: pctN(d.fail, d.total) }));
  }, [filtered]);

  const emptyState = rawData.length === 0;
  const curNav = NAV.find(n => n.id === tab) || NAV[0];

  return (
    <div style={{ display: "flex", minHeight: `${100 / ZOOM}vh`, fontFamily: "'Inter',system-ui,-apple-system,sans-serif", background: T.bg, zoom: ZOOM }}>
      {/* ── SIDEBAR ── */}
      <div style={{
        width: 220, background: "linear-gradient(180deg, #0f172a 0%, #0b1120 100%)", position: "fixed", top: 0, left: 0, height: `${100 / ZOOM}vh`,
        display: "flex", flexDirection: "column", zIndex: 100, boxShadow: "2px 0 20px rgba(0,0,0,.15)"
      }}>
        {/* Logo FutureDial */}
        <div style={{ padding: "18px 16px 14px", borderBottom: "1px solid #1e293b", position: "relative" }}>
          <img src="/futuredial-logo.png" alt="FutureDial" style={{ width: "100%", maxWidth: 174, display: "block", filter: "brightness(0) invert(1)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, paddingLeft: 1, color: "#818cf8" }}>
            <AppIcon icon="🏥" size={11} />
            <span style={{ color: "#94a3b8", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>RADI Pulse</span>
          </div>
          <div className="header-pulse-line" style={{ position: "absolute", bottom: -1, left: 16, right: 16, height: 2, background: "linear-gradient(90deg, #6366f1, #8b5cf6, transparent)", borderRadius: 2 }} />
        </div>
        {/* Upload */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #1e293b" }}>
          <button onClick={() => fileRef.current.click()} className="hover-lift" style={{
            width: "100%", background: loading ? "#1e293b" : "rgba(37,99,235,.2)",
            border: "1px solid rgba(37,99,235,.4)", color: loading ? "#475569" : "#93c5fd",
            borderRadius: 8, padding: "9px", cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: .5,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 7
          }}>
            {loading
              ? (<><AppIcon icon="⏳" size={13} spin /><span>Loading...</span></>)
              : (<><AppIcon icon="📂" size={13} /><span>Upload XL File</span></>)}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleFile} />

          <div style={{ position: "relative", marginTop: 8 }}>
            <button onClick={() => setShowApiMenu(v => !v)} className="hover-lift" style={{
              width: "100%", background: apiLoading ? "#1e293b" : "rgba(16,185,129,.15)",
              border: "1px solid rgba(16,185,129,.4)", color: apiLoading ? "#475569" : "#6ee7b7",
              borderRadius: 8, padding: "9px", cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: .5,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7
            }}>
              {apiLoading
                ? (<><AppIcon icon="⏳" size={13} spin /><span>{apiSyncMessage || "Syncing..."}</span></>)
                : (<><AppIcon icon="🔌" size={13} /><span>Sync from RADI API</span></>)}
            </button>
            {showApiMenu && !apiLoading && (
              <div className="pop-in" style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, background: "rgba(15,23,42,.92)",
                backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
                border: "1px solid #334155", borderRadius: 8, padding: 10, zIndex: 200, boxShadow: "0 8px 28px rgba(0,0,0,.5)",
                width: 520
              }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {/* ── LEFT COLUMN: clients + time basis ── */}
                  <div>
                    <div style={{ color: "#94a3b8", fontSize: 10, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>
                      Client compan{clients.length === 1 ? "y" : "ies"}
                    </div>
                    {clientsError && (
                      <div style={{ color: "#fca5a5", fontSize: 11, marginBottom: 8 }}>
                        {clientsError}
                        {clientsAuthExpired && (
                          <div style={{ marginTop: 6 }}>
                            <button
                              onClick={() => instance.loginRedirect(loginRequest)}
                              style={{
                                background: "#1e293b", border: "1px solid #475569", color: "#e2e8f0",
                                borderRadius: 6, padding: "5px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600
                              }}
                            >Sign in again</button>
                          </div>
                        )}
                      </div>
                    )}
                    {!clientsError && clients.length === 0 && <div style={{ color: "#64748b", fontSize: 11, marginBottom: 8 }}>Loading…</div>}
                    {clients.length > 0 && (
                      <>
                        <div style={{ maxHeight: 168, overflowY: "auto", marginBottom: 4, border: "1px solid #334155", borderRadius: 6, padding: 4 }}>
                          {clients.map(c => {
                            const checked = selectedClients.includes(c.key);
                            return (
                              <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 4px", fontSize: 12, color: "#cbd5e1", cursor: "pointer" }}>
                                <input type="checkbox" checked={checked} onChange={() => {
                                  setSelectedClients(sel => checked ? sel.filter(k => k !== c.key) : [...sel, c.key]);
                                }} />
                                {c.label}
                              </label>
                            );
                          })}
                        </div>
                        {clients.length > 1 && (
                          <button onClick={() => setSelectedClients(selectedClients.length === clients.length ? [] : clients.map(c => c.key))} style={{
                            background: "transparent", border: "1px solid #334155", color: "#94a3b8",
                            borderRadius: 6, padding: "3px 8px", fontSize: 10, cursor: "pointer", marginBottom: 10
                          }}>
                            {selectedClients.length === clients.length ? "Deselect all" : "Select all"}
                          </button>
                        )}
                      </>
                    )}

                    <div style={{ color: "#94a3b8", fontSize: 10, margin: "4px 0 6px", textTransform: "uppercase", letterSpacing: 1 }}>Time basis</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {[["2", "🖥️", "Server time"], ["1", "📱", "Transaction time"]].map(([v, ic, l]) => (
                        <button type="button" key={v} onClick={() => setApiTimeType(v)} style={{
                          padding: "6px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", textAlign: "left",
                          background: apiTimeType === v ? "rgba(16,185,129,.2)" : "transparent",
                          border: `1px solid ${apiTimeType === v ? "rgba(16,185,129,.5)" : "#334155"}`,
                          color: apiTimeType === v ? "#6ee7b7" : "#94a3b8",
                          display: "flex", alignItems: "center", gap: 7
                        }}><AppIcon icon={ic} size={11} />{l}</button>
                      ))}
                    </div>
                  </div>

                  {/* ── RIGHT COLUMN: date range + calendar + time ── */}
                  <div>
                    <div style={{ color: "#94a3b8", fontSize: 10, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Date range</div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      {[["today", "Today"], ["7d", "Last 7 days"], ["29d", "Last 29 days"]].map(([kind, label]) => (
                        <button type="button" key={kind} onClick={() => applyQuickRange(kind)} style={{
                          flex: 1, background: "transparent", border: "1px solid #334155", color: "#94a3b8",
                          borderRadius: 6, padding: "4px 6px", fontSize: 10, cursor: "pointer"
                        }}>{label}</button>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      {["start", "end"].map(f => {
                        const val = f === "start" ? apiStart : apiEnd;
                        const d = new Date(val);
                        const ok = !isNaN(d);
                        const active = activeDateField === f;
                        return (
                          <button type="button" key={f} onClick={() => setActiveDateField(f)} style={{
                            flex: 1, textAlign: "left", padding: "6px 8px", borderRadius: 6, cursor: "pointer",
                            background: active ? "rgba(16,185,129,.15)" : "#0f172a",
                            border: `1px solid ${active ? "rgba(16,185,129,.5)" : "#334155"}`,
                          }}>
                            <div style={{ fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{f === "start" ? "From" : "To"}</div>
                            <div style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 700 }}>{ok ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>{ok ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                          </button>
                        );
                      })}
                    </div>

                    {(() => {
                      const val = activeDateField === "start" ? apiStart : apiEnd;
                      const setVal = activeDateField === "start" ? setApiStart : setApiEnd;
                      const { d: dPart, t: tPart } = splitDateTime(val);
                      const dateObj = dPart ? new Date(dPart + "T00:00:00") : null;
                      const [hh, mm] = (tPart || "00:00").split(":");
                      const selStyle = {
                        background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0",
                        borderRadius: 6, padding: "4px 6px", fontSize: 12
                      };
                      return (
                        <div>
                          <MiniCalendar selected={dateObj} onSelect={newDate => {
                            const p = n => String(n).padStart(2, "0");
                            const newDatePart = `${newDate.getFullYear()}-${p(newDate.getMonth() + 1)}-${p(newDate.getDate())}`;
                            setVal(joinDateTime(newDatePart, tPart));
                          }} />
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                            <span style={{ fontSize: 11, color: "#94a3b8", display: "flex", alignItems: "center", gap: 5 }}><AppIcon icon="🕐" size={11} /> Time</span>
                            <select value={hh} onChange={e => setVal(joinDateTime(dPart, `${e.target.value}:${mm}`))} style={selStyle}>
                              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                            <span style={{ color: "#64748b" }}>:</span>
                            <select value={mm} onChange={e => setVal(joinDateTime(dPart, `${hh}:${e.target.value}`))} style={selStyle}>
                              {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* ── FULL-WIDTH FOOTER: duration hint + fetch button ── */}
                {(() => {
                  const ms = new Date(apiEnd) - new Date(apiStart);
                  const days = ms / 86400000;
                  // FutureDial only allows ~30 days per underlying request, but the
                  // backend now automatically splits longer ranges into several
                  // 28-day batches (a bit under FutureDial's own limit, since an
                  // exact 30-day request still got rejected in testing) and merges
                  // the results — so anything up to a year is allowed here, just
                  // flagged as "will run in batches".
                  const CHUNK_DAYS = 28;
                  const MAX_DAYS = 366;
                  const chunky = ms > 0 && days > CHUNK_DAYS;
                  const tooLong = ms > 0 && days > MAX_DAYS;
                  const invalid = !(ms > 0);
                  const chunkCount = chunky ? Math.ceil(days / CHUNK_DAYS) : 1;
                  return (
                    <div style={{ fontSize: 10.5, margin: "10px 0 8px", color: invalid || tooLong ? "#fca5a5" : chunky ? "#fbbf24" : "#64748b" }}>
                      {invalid
                        ? "End must be after start."
                        : tooLong
                        ? `${days.toFixed(1)} days selected — max is ${MAX_DAYS} days (about a year) per sync.`
                        : chunky
                        ? `${days.toFixed(1)} days selected — will run as ${chunkCount} batches of ≤${CHUNK_DAYS} days each (can take a while for many clients).`
                        : `${days.toFixed(1)} day(s) selected`}
                    </div>
                  );
                })()}

                <button onClick={handleApiSync} disabled={selectedClients.length === 0 || !(new Date(apiEnd) - new Date(apiStart) > 0) || (new Date(apiEnd) - new Date(apiStart)) / 86400000 > 366} className={selectedClients.length === 0 ? undefined : "hover-lift"} style={{
                  width: "100%",
                  background: selectedClients.length === 0 ? "#334155" : "linear-gradient(135deg, #10b981, #059669)",
                  color: selectedClients.length === 0 ? "#64748b" : "#052e1f", border: "none",
                  borderRadius: 6, padding: "8px", cursor: selectedClients.length === 0 ? "not-allowed" : "pointer",
                  fontSize: 12, fontWeight: 700,
                  boxShadow: selectedClients.length === 0 ? "none" : "0 2px 10px rgba(16,185,129,.35)"
                }}>Fetch now</button>
              </div>
            )}
          </div>

          {rawData.length > 0 && (
            <div style={{ marginTop: 8, textAlign: "center", fontSize: 10, color: "#475569" }}>
              {filtered.length.toLocaleString()} / {rawData.length.toLocaleString()} rows
            </div>
          )}
        </div>
        {/* Nav */}
        <nav style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}>
          {NAV.map(n => {
            const active = tab === n.id;
            return (
              <button key={n.id} onClick={() => setTab(n.id)} className="nav-btn" style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                borderRadius: 8, marginBottom: 2, cursor: "pointer", textAlign: "left", fontSize: 13,
                background: active ? "linear-gradient(135deg, rgba(99,102,241,.28), rgba(139,92,246,.16))" : "transparent",
                borderLeft: active ? "3px solid #818cf8" : "3px solid transparent",
                borderTop: "1px solid transparent", borderRight: "1px solid transparent", borderBottom: "1px solid transparent",
                color: active ? "#c7d2fe" : "#94a3b8", fontWeight: active ? 700 : 400
              }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = T.sidebarHov; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                <span style={{ width: 20, display: "flex", justifyContent: "center", color: active ? "#a5b4fc" : "#64748b" }}>
                  <AppIcon icon={n.icon} size={15} />
                </span>
                {n.label}
              </button>
            );
          })}
        </nav>
        {/* Signed-in user + sign out — the whole point of Microsoft sign-in
            is pointless if it's invisible, so we show who it thinks you are. */}
        <div style={{ padding: "10px 16px", borderTop: "1px solid #1e293b" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, minWidth: 0 }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700
            }}>
              {(account?.name || account?.username || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#cbd5e1", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {account?.name || "Signed in"}
              </div>
              <div style={{ color: "#475569", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {account?.username || ""}
              </div>
            </div>
          </div>
          <button
            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
            className="hover-lift"
            style={{
              width: "100%", background: "transparent", border: "1px solid #334155", color: "#94a3b8",
              borderRadius: 6, padding: "6px", cursor: "pointer", fontSize: 11, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6
            }}
          ><AppIcon icon="↩" size={12} /> Sign out</button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ marginLeft: 220, flex: 1, display: "flex", flexDirection: "column", minHeight: `${100 / ZOOM}vh`, minWidth: 0, maxWidth: `calc(${100 / ZOOM}vw - 220px)` }}>
        {/* Topbar */}
        <div style={{
          background: T.card, borderBottom: `1px solid ${T.border}`, padding: "14px 28px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, zIndex: 50, boxShadow: "0 1px 4px rgba(0,0,0,.06)"
        }}>
          <div>
            <div style={{ color: T.text, fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <AppIcon icon={curNav.icon} size={16} />
              {curNav.label}
            </div>
            {rawData.length > 0 && <div style={{ color: T.textLight, fontSize: 11, marginTop: 1 }}>
              {filtered.length} filtered rows · {rawData.length} total
            </div>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {error && <div style={{ background: T.failBg, color: T.fail, borderRadius: 8, padding: "6px 14px", fontSize: 12 }}>{error}</div>}
            {filtered.length > 0 && (
              <button onClick={() => setShowReportModal(true)} className="hover-lift" style={{
                background: "linear-gradient(135deg,#7c3aed,#6d28d9)", color: "#fff", border: "none",
                borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700,
                boxShadow: "0 2px 8px rgba(109,40,217,.4)", display: "flex", alignItems: "center", gap: 6
              }}><AppIcon icon="📄" size={13} />PDF Report</button>
            )}
          </div>
        </div>
        {showReportModal && <ReportModal filtered={filtered} onClose={() => setShowReportModal(false)} />}

        {/* Content */}
        <div key={tab} className="fade-in-tab" style={{ flex: 1, padding: "24px 28px", overflowX: "hidden", minWidth: 0 }}>
          {emptyState ? (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              minHeight: "60vh", gap: 16, textAlign: "center"
            }}>
              <div style={{ width: 80, height: 80, borderRadius: 20, background: "linear-gradient(135deg, #eff6ff, #ede9fe)", display: "flex", alignItems: "center", justifyContent: "center", color: T.accent }}><AppIcon icon="📡" size={34} strokeWidth={1.5} /></div>
              <div style={{ color: T.text, fontSize: 20, fontWeight: 700 }}>No Data Loaded</div>
              <div style={{ color: T.textMid, fontSize: 13, maxWidth: 380 }}>Upload an Excel or CSV file exported from FutureDial / RADI, or use "Sync from RADI API" in the sidebar to pull data automatically</div>
              <button onClick={() => fileRef.current.click()} className="hover-lift" style={{
                background: "linear-gradient(135deg, #2563eb, #4f46e5)", color: "#fff",
                border: "none", borderRadius: 10, padding: "12px 28px", cursor: "pointer", fontSize: 14, fontWeight: 700,
                boxShadow: "0 4px 12px rgba(37,99,235,.35)", display: "flex", alignItems: "center", gap: 8
              }}><AppIcon icon="📂" size={14} /> Upload Test Results</button>
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                <KpiCard label="Total Transactions" value={kpis.total.toLocaleString()} icon="📊" />
                <KpiCard label="Failures" value={kpis.failures.toLocaleString()} color={T.fail} icon="❌" />
                <KpiCard label="Fail Rate" value={kpis.failPct} color={T.warn} icon="📉" />
                <KpiCard label="Models" value={kpis.models} color={T.purple} icon="📱" />
                <KpiCard label="Stations" value={kpis.stations} color={T.pass} icon="🏭" />
                <KpiCard label="Avg Test Duration" value={kpis.avgDurationMin != null ? fmtDuration(kpis.avgDurationMin) : "—"} color="#06b6d4" icon="⏱" />
              </div>

              <FilterBar data={rawData} filters={filters} setFilters={setFilters} failOnlyTests={failOnlyTests} />

              {/* Trend — overview only */}
              {tab === "overview" && trendData.length > 1 && (
                <Card style={{ marginBottom: 20, padding: "14px 8px" }}>
                  <ChartTitle>Failure Rate Trend</ChartTitle>
                  <ResponsiveContainer width="100%" height={140}>
                    <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                      <XAxis dataKey="date" tick={{ fill: T.textMid, fontSize: 10 }} /><YAxis tick={{ fill: T.textMid, fontSize: 10 }} unit="%" domain={[0, 100]} />
                      <Tooltip contentStyle={TS} formatter={v => [v + "%", "Fail Rate"]} />
                      <Line type="monotone" dataKey="failPct" stroke={T.fail} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {tab === "overview" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                  <Card>
                    <ChartTitle>Failures by Model (Top 12)</ChartTitle>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={result2.sort((a, b) => b.failCount - a.failCount).slice(0, 12)} layout="vertical" margin={{ left: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                        <XAxis type="number" tick={{ fill: T.textMid, fontSize: 10 }} /><YAxis type="category" dataKey="label" tick={{ fill: T.text, fontSize: 10 }} width={80} />
                        <Tooltip contentStyle={TS} /><Bar dataKey="failCount" fill={T.warn} radius={[0, 4, 4, 0]} name="Failures" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                  <Card>
                    <ChartTitle>Top Failure Reasons</ChartTitle>
                    {(() => {
                      const reasons = {};
                      filtered.forEach(r => {
                        const f = r["result_FAIL"] || "";
                        if (!f) return;
                        f.split(",").forEach(v => {
                          const name = v.trim(); if (name) reasons[name] = (reasons[name] || 0) + 1;
                        });
                      });
                      const sorted = Object.entries(reasons).sort((a, b) => b[1] - a[1]).slice(0, 10);
                      const max = sorted[0]?.[1] || 1;
                      return sorted.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {sorted.map(([reason, cnt]) => (
                            <div key={reason} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, color: T.text, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{reason}</div>
                              <div style={{ width: 80, height: 6, background: T.border, borderRadius: 3, overflow: "hidden" }}>
                                <div style={{ width: `${(cnt / max) * 100}%`, height: "100%", background: T.fail, borderRadius: 3 }} />
                              </div>
                              <div style={{ color: T.fail, fontSize: 12, fontWeight: 700, minWidth: 28, textAlign: "right" }}>{cnt}</div>
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ color: T.textLight, fontSize: 12 }}>No result_FAIL data in this dataset</div>;
                    })()}
                  </Card>
                </div>
              )}

              <ErrorBoundary key={tab}>


              {tab === "tests" && <TestAnalysisTab data={filtered} />}
              {tab === "cross" && <CrossStationTab data={filtered} />}
              {tab === "health" && <StationHealthTab data={filtered} />}
              {tab === "global" && <GlobalAnalysisTab data={filtered} />}
              {tab === "repeat" && <RepeatDevicesTab data={filtered} />}
              {tab === "time" && <TimePatternsTab data={filtered} />}
              {tab === "skip" && <SkipRateTab data={filtered} />}
              {tab === "duration" && <TestDurationTab data={filtered} />}
              {tab === "idle" && <IdleTimeTab data={filtered} />}

              {tab === "raw" && (
                <>
                  <SectionTitle>Raw Data ({filtered.length} rows)</SectionTitle>
                  <div style={{ marginBottom: 12, position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.textLight, display: "flex", pointerEvents: "none" }}>
                      <AppIcon icon="🔍" size={13} />
                    </span>
                    <input value={rawSearch} onChange={e => setRawSearch(e.target.value)}
                      placeholder="Search across all visible columns..."
                      style={{
                        width: "100%", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
                        color: T.text, padding: "9px 14px 9px 34px", fontSize: 12, outline: "none", boxSizing: "border-box",
                        boxShadow: "0 1px 3px rgba(0,0,0,.04)"
                      }} />
                  </div>
                  <DataTable search={rawSearch} columns={[
                    { key: "Transaction ID", label: "Transaction ID", mono: true }, { key: "_dateStr", label: "Date" },
                    { key: "Model", label: "Model" }, { key: "IMEI", label: "IMEI", mono: true },
                    { key: "Manufacturer", label: "Manufacturer" }, { key: "OS", label: "OS" },
                    { key: "Station ID", label: "Station" }, { key: "Site", label: "Site" },
                    { key: "Overall status", label: "Status", badge: true }, { key: "result_FAIL", label: "Fail Reason" },
                    { key: "Operator", label: "Operator" }, { key: "App version", label: "App ver" }
                  ]} rows={filtered} maxRows={150} />
                </>
              )}

              </ErrorBoundary>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// SIGN-IN GATE (Microsoft Entra ID)
// ══════════════════════════════════════════════════
// This dashboard shows real device-test data and, via "Sync from RADI API",
// can pull directly from FutureDial using this organization's credentials —
// so nothing below renders until Microsoft confirms who's asking. App() is
// what actually gets exported/mounted; the entire dashboard (Dashboard(),
// above) only ever mounts once useIsAuthenticated() is true.
function SignInScreen() {
  const { instance, inProgress } = useMsal();
  const [signInError, setSignInError] = useState("");
  const busy = inProgress !== InteractionStatus.None;

  const handleSignIn = () => {
    setSignInError("");
    instance.loginRedirect(loginRequest).catch(err => {
      setSignInError(err?.message || "Sign-in failed — please try again.");
    });
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(160deg, #0f172a 0%, #1e1b4b 55%, #0b1120 100%)",
      fontFamily: "'Inter',system-ui,-apple-system,sans-serif", padding: 20
    }}>
      <div style={{
        width: "100%", maxWidth: 380, background: "rgba(15,23,42,.6)", backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)", border: "1px solid rgba(148,163,184,.15)", borderRadius: 16,
        padding: "36px 32px", boxShadow: "0 20px 60px rgba(0,0,0,.45)", textAlign: "center"
      }}>
        <img src="/futuredial-logo.png" alt="FutureDial" style={{ width: 150, margin: "0 auto 22px", display: "block", filter: "brightness(0) invert(1)" }} />
        <div style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>RADI Pulse</div>
        <div style={{ color: "#64748b", fontSize: 12.5, marginBottom: 28 }}>Sign in with your organization account to continue</div>

        <button onClick={handleSignIn} disabled={busy} className="hover-lift" style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          background: busy ? "#1e293b" : "#fff", color: busy ? "#475569" : "#1e293b",
          border: "none", borderRadius: 8, padding: "11px", fontSize: 13.5, fontWeight: 700,
          cursor: busy ? "wait" : "pointer", boxShadow: busy ? "none" : "0 2px 10px rgba(0,0,0,.25)"
        }}>
          {busy ? (
            <><AppIcon icon="⏳" size={15} spin /><span>Signing in…</span></>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>
              <span>Sign in with Microsoft</span>
            </>
          )}
        </button>

        {signInError && <div style={{ color: "#fca5a5", fontSize: 11.5, marginTop: 14 }}>{signInError}</div>}

        <div style={{ color: "#334155", fontSize: 10, marginTop: 26, letterSpacing: .5 }}>
          Access is limited to accounts in this organization's Microsoft 365 directory
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  // While MSAL is still working out whether a redirect just came back with a
  // token (or is silently checking an existing session), show a plain
  // loading screen instead of flashing the sign-in button and then
  // immediately replacing it with the dashboard.
  if (inProgress !== InteractionStatus.None && !isAuthenticated) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "#0f172a", color: "#64748b", fontFamily: "'Inter',system-ui,-apple-system,sans-serif", fontSize: 13
      }}>
        <AppIcon icon="⏳" size={16} spin /> <span style={{ marginLeft: 8 }}>Checking sign-in…</span>
      </div>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>
        <Dashboard />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInScreen />
      </UnauthenticatedTemplate>
    </>
  );
}